import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useUrlTableState from '../../../hooks/useUrlTableState';
import { useAuth } from '../../../context/AuthContext';
import PermissionGate from '../../../components/common/PermissionGate';
import { API_BASE_URL } from '../../../api/config';
import candidateService from '../../../api/services/candidateService';
import jobService from '../../../api/services/jobService';
import interviewService from '../../../api/services/interviewService';
import locationService from '../../../api/services/locationService';
import hrService from '../../../api/services/hrService';
import offerService from '../../../api/services/offerService';
import { getSalaryStructures } from '../../../api/salaryStructureApi';
import toast from 'react-hot-toast';
import CreateOfferModal from '../../../components/recruitment/CreateOfferModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV, generateExportFilename } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  GraduationCap,
  Clock,
  User,
  ChevronRight,
  X,
  MoreVertical,
  Download,
  XCircle,
  RefreshCw,
  FileText,
  Building,
  Globe,
  Linkedin,
  Github,
  Star,
  Calendar,
  Award,
  IndianRupee,
  CheckCircle,
  ExternalLink,
  AlertCircle,
  LayoutDashboard,
  LayoutList,
  Video,
  ArrowRight,
  CheckSquare,
  Square,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import ScheduleInterviewModal from '../../../components/recruitment/ScheduleInterviewModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const PIPELINE_BATCH_SIZE = 5;
const LIST_VIEW_PAGE_SIZE = 10;
const PIPELINE_SCROLL_THRESHOLD = 120;
const MAX_JOB_OPTION_LENGTH = 30;
// Limit pipeline fetch payload to explicitly requested summary fields.
const PIPELINE_ALLOWED_FIELDS = [
  'name',
  'firstName',
  'lastName',
  'department',
  'email',
  'phone',
  'location',
  'experience',
  'skills',
  'appliedDate',
  'candidateId',
  'job',
  'jobId',
  'jobTitle',
  '_id'
];
const PIPELINE_FIELDS_PARAM = PIPELINE_ALLOWED_FIELDS.join(',');

const truncateJobOptionLabel = (label, maxLength = MAX_JOB_OPTION_LENGTH) => {
  if (!label) return '';
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 5)}...`;
};

const truncateText = (value = '', maxLength = 24) => {
  if (!value) return '';
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(maxLength - 3, 0))}...`;
};
// NOTE: Legacy candidate detail modal retained for reference only.
const CandidateDetailsModal = ({
  selectedCandidate,
  onClose,
  stages,
  openPdfViewer,
  pdfLoading,
  isLoadingDetails,
  detailsError
}) => {

  // Helper function to get rating stars
  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} className="h-4 w-4 text-yellow-400 fill-current opacity-50" />);
      } else {
        stars.push(<Star key={i} className="h-4 w-4 text-gray-300" />);
      }
    }
    return stars;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'hired': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'backout': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get availability color
  const getAvailabilityColor = (availability) => {
    switch (availability?.toLowerCase()) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'not available': return 'bg-red-100 text-red-800';
      case 'busy': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const jobIdDisplay =
    selectedCandidate?.jobId ||
    selectedCandidate?.jobIdString ||
    selectedCandidate?.job?.jobId ||
    selectedCandidate?.job?._id ||
    'Not specified';
  const jobDepartment =
    selectedCandidate?.jobDepartment ||
    selectedCandidate?.department ||
    selectedCandidate?.job?.department ||
    'Not specified';
  const jobLocation =
    selectedCandidate?.jobLocation ||
    selectedCandidate?.location ||
    selectedCandidate?.job?.location ||
    'Not specified';
  const jobType =
    selectedCandidate?.jobType ||
    selectedCandidate?.job?.jobType ||
    selectedCandidate?.job?.employmentType ||
    'Not specified';
  const jobExperience = selectedCandidate.experience?.name || 'N/A';
  const jobNoticePeriod = selectedCandidate?.noticePeriod
    ? selectedCandidate.noticePeriod === 'immediate'
      ? 'Immediate'
      : selectedCandidate.noticePeriod
    : 'N/A';
  const appliedDateDisplay = selectedCandidate?.appliedDate
    ? formatDate(selectedCandidate.appliedDate, { includeTime: false })
    : 'N/A';
  const expectedSalaryDisplay =
    selectedCandidate?.expectedSalary && selectedCandidate.expectedSalary !== 'Not specified'
      ? selectedCandidate.expectedSalary
      : 'N/A';
  const candidateAvailability = selectedCandidate?.availability || 'N/A';
  const candidateSource = selectedCandidate?.source || 'Not specified';
  const jobTitleDisplay = selectedCandidate?.jobTitle || 'Not specified';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto relative">
        {isLoadingDetails && (
          <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-20">
            <div className="flex items-center space-x-2 text-gray-600">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Loading detailed information...</span>
            </div>
          </div>
        )}
        <div className="p-6">
          {detailsError && !isLoadingDetails && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded text-sm text-red-700">
              {detailsError || 'Failed to load detailed candidate information. Showing basic data.'}
            </div>
          )}
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-brand rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div className="space-y-1 min-w-0">
                <h2 className="text-2xl font-bold text-gray-900 capitalize break-words break-all">
                  {selectedCandidate?.firstName} {selectedCandidate?.lastName}
                </h2>
                <p className="text-gray-600 text-sm break-words break-all">
                  {selectedCandidate?.candidateId || selectedCandidate?.id}
                </p>
                <p className="text-sm text-[#0F2942] break-words break-all">
                  {selectedCandidate?.email || 'Email not provided'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {/* Status and Rating */}
          <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedCandidate?.status)}`}>
                <CheckCircle className="h-4 w-4 mr-1" />
                <span className="capitalize">{selectedCandidate?.status || 'Active'}</span>
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getAvailabilityColor(selectedCandidate?.availability)}`}>
                <Clock className="h-4 w-4 mr-1" />
                <span className="capitalize">{candidateAvailability}</span>
              </span>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-6 lg:col-span-2">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-brand" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900">
                        {selectedCandidate?.email || 'Not provided'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Phone className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Phone</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900">
                        {selectedCandidate?.phone || 'Not provided'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Location</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {selectedCandidate?.location || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Source</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {candidateSource || 'Manual Entry'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Assignment */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-brand" />
                  Job Assignment
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Job Title</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {jobTitleDisplay}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Job ID</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900">
                        {jobIdDisplay}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Building className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Department</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {jobDepartment}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Location</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {jobLocation}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Job Type</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {jobType}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 mb-1">Notice Period</p>
                      <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                        {jobNoticePeriod}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Professional Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2 text-brand" />
                    Professional Information
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Job Position</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                          {selectedCandidate?.jobTitle || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Building className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Department</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                          {selectedCandidate?.department || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Clock className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Experience</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900">
                          {selectedCandidate.experience?.name || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Building className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Current Company</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                          {selectedCandidate?.currentCompany || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Clock className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Notice Period</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                          {selectedCandidate?.noticePeriod ? `${selectedCandidate.noticePeriod}` : 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Education */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <GraduationCap className="h-5 w-5 mr-2 text-brand" />
                    Education
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <GraduationCap className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Education</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                          {selectedCandidate?.education || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Building className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">University</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900 capitalize">
                          {selectedCandidate?.university || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Graduation Year</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900">
                          {selectedCandidate?.graduationYear || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Salary Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <IndianRupee className="h-5 w-5 mr-2 text-brand" />
                    Salary Information
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <span className="text-gray-400 font-bold text-lg flex-shrink-0 mt-0.5">₹</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Expected Salary</p>
                        <p className="text-sm font-medium break-words break-all text-gray-900">
                          {expectedSalaryDisplay}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Globe className="h-5 w-5 mr-2 text-brand" />
                    Social Links
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Linkedin className="h-4 w-4 text-[#0F2942] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">LinkedIn</p>
                        {selectedCandidate?.linkedin ? (
                          <a
                            href={selectedCandidate.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#0F2942] hover:text-blue-800 break-words break-all block underline"
                          >
                            {selectedCandidate.linkedin}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-400">Not provided</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Github className="h-4 w-4 text-gray-800 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">GitHub</p>
                        {selectedCandidate?.github ? (
                          <a
                            href={selectedCandidate.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gray-800 hover:text-gray-600 break-words break-all block underline"
                          >
                            {selectedCandidate.github}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-400">Not provided</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Globe className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 mb-1">Portfolio</p>
                        {selectedCandidate?.portfolio ? (
                          <a
                            href={selectedCandidate.portfolio}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-green-600 hover:text-green-800 break-words break-all block underline"
                          >
                            {selectedCandidate.portfolio}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-400">Not provided</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div className="space-y-6">
              {/* Resume Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-brand" />
                  Resume
                </h3>
                <div className="flex items-start space-x-3">
                  {selectedCandidate?.resume ? (
                    <div className="flex items-start space-x-2 flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800 break-words flex-1">
                        {selectedCandidate.resume?.originalName ||
                          selectedCandidate.resume?.filename ||
                          selectedCandidate.resume?.name ||
                          'Resume.pdf'}
                      </span>
                      <button
                        onClick={() => {
                          try {
                            let fileUrl;
                            if (selectedCandidate.resume?.url) {
                              fileUrl = selectedCandidate.resume.url;
                            } else if (typeof selectedCandidate.resume === 'string') {
                              fileUrl = selectedCandidate.resume;
                            } else {
                              toast.error('Document not found', { icon: '⚠️' });
                              return;
                            }

                            const link = document.createElement('a');
                            link.target = '_blank';
                            link.download =
                              selectedCandidate.resume?.originalName || selectedCandidate.resume?.filename || 'resume.pdf';

                            if (!fileUrl.startsWith('http')) {
                              const baseUrl = API_BASE_URL.replace('/api', '');
                              link.href = `${baseUrl}${fileUrl}`;
                            } else {
                              link.href = fileUrl;
                            }

                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          } catch (err) {
                            toast.error('Error downloading resume: ' + (err?.message || 'Unknown error'));
                          }
                        }}
                        className="text-[#0F2942] hover:text-blue-800 flex-shrink-0 p-1 hover:bg-blue-50 rounded transition-colors"
                        title="Download Resume"
                        disabled={false}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No resume uploaded</span>
                  )}
                </div>
              </div>

              {/* Skills */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-brand" />
                  Skills
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCandidate?.skills && selectedCandidate.skills.length > 0 ? (
                    selectedCandidate.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm break-all whitespace-normal capitalize max-w-full"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">No skills specified</p>
                  )}
                </div>
              </div>

              {/* Application Timeline */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-brand" />
                  Application Timeline
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Applied</span>
                    <span className="text-sm font-medium">
                      {selectedCandidate?.appliedDate
                        ? formatDate(selectedCandidate.appliedDate, { includeTime: false })
                        : 'Not available'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Last Activity</span>
                    <span className="text-sm font-medium">
                      {selectedCandidate?.lastActivity
                        ? formatDate(selectedCandidate.lastActivity, { includeTime: false })
                        : 'Not available'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">Current Stage</span>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${stages.find((s) => s.id === selectedCandidate?.stage)?.color || 'bg-gray-100 text-gray-800'
                        }`}
                    >
                      {stages.find((s) => s.id === selectedCandidate?.stage)?.name || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>

              {(selectedCandidate?.notes || (Array.isArray(selectedCandidate?.tags) && selectedCandidate.tags.length > 0)) && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-brand" />
                    Additional Information
                  </h3>
                  {selectedCandidate?.notes && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-800 break-words break-all whitespace-pre-wrap">
                        {selectedCandidate.notes}
                      </p>
                    </div>
                  )}
                  {Array.isArray(selectedCandidate?.tags) && selectedCandidate.tags.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCandidate.tags.map((tag, index) => (
                          <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold break-words break-all">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(selectedCandidate?.documents) && selectedCandidate.documents.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-brand" />
                    Supporting Documents
                  </h3>
                  <ul className="space-y-3">
                    {selectedCandidate.documents.map((doc, index) => (
                      <li key={doc.id || doc._id || `doc-${index}`} className="flex items-start space-x-3">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 break-words break-all">
                            {doc.name || doc.originalName || doc.filename || `Document ${index + 1}`}
                          </p>
                          {doc.url && (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-[#0F2942] hover:text-blue-800 break-words break-all underline"
                            >
                              View document
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


const normalizeSkills = (skills) => {
  if (Array.isArray(skills)) {
    return skills.filter(Boolean);
  }
  if (typeof skills === 'string') {
    return skills
      .split(',')
      .map((skill) => skill.trim())
      .filter(Boolean);
  }
  return [];
};

const resolveStage = (candidate) => {
  const status = candidate?.status || candidate?.recruitmentStatus || candidate?.stage || 'applied';
  return status === 'offered' ? 'offer' : status;
};

const mapCandidateSummary = (candidate = {}) => {
  const skills = normalizeSkills(candidate.skills);
  const resolvedName =
    candidate.name ||
    [candidate.firstName, candidate.lastName].filter(Boolean).join(' ').trim() ||
    'Unknown';

  return {
    id: candidate.id || candidate._id || candidate.candidateId,
    _id: candidate._id || candidate.id,
    candidateId: candidate.candidateId || candidate.candidateCode || candidate.id,
    name: resolvedName,
    firstName: candidate.firstName || '',
    lastName: candidate.lastName || '',
    email: candidate.email || '',
    phone: candidate.phone || '',
    jobTitle: candidate.jobTitle || candidate.job?.title || 'Not specified',
    job: candidate.job || candidate.jobId || candidate.job?._id || null, // MongoDB ObjectId for interview scheduling
    jobId:
      candidate.jobId ||
      candidate.job ||
      candidate.job?.jobId ||
      candidate.job?._id ||
      candidate.jobIdString ||
      '',
    jobIdString: candidate.jobIdString || candidate.jobId || '',
    stage: resolveStage(candidate),
    experience: candidate.experience?.name || 'N/A',
    location: candidate.location || candidate.jobLocation || 'Not specified',
    jobDepartment:
      candidate.jobDepartment ||
      candidate.department ||
      candidate.job?.department ||
      '',
    jobLocation:
      candidate.jobLocation ||
      candidate.job?.location ||
      candidate.location ||
      '',
    jobType:
      candidate.jobType ||
      candidate.job?.jobType ||
      candidate.job?.employmentType ||
      candidate.employmentType ||
      '',
    skills,
    appliedDate: candidate.appliedDate || candidate.createdAt || null,
    lastActivity: candidate.lastActivity || candidate.updatedAt || null,
    status: candidate.status || candidate.recruitmentStatus || 'active',
    availability: candidate.availability || 'Available',
    rating: candidate.rating || 0,
    source: candidate.source || 'manual',
    department: candidate.department || '',
    noticePeriod: candidate.noticePeriod || '',
    offerStatus: candidate.offerStatus || null,
    offerId: candidate.offerId || null,
  };
};

const mapCandidateDetails = (candidate = {}) => {
  const summary = mapCandidateSummary(candidate);
  return {
    ...summary,
    currentCompany: candidate.currentCompany || '',
    expectedSalary: candidate.expectedSalary || '',
    currentSalary: candidate.currentSalary || '',
    salaryRange: candidate.salaryRange || '',
    noticePeriod: candidate.noticePeriod || summary.noticePeriod || '',
    education: candidate.education || '',
    university: candidate.university || '',
    graduationYear: candidate.graduationYear || '',
    resume: candidate.resume || null,
    linkedin: candidate.linkedin || '',
    github: candidate.github || '',
    portfolio: candidate.portfolio || '',
    notes: candidate.notes || '',
    attachments: candidate.attachments || [],
    address: candidate.address || '',
    source: candidate.source || summary.source,
    department: candidate.department || summary.department,
    jobDepartment: candidate.jobDepartment || summary.jobDepartment,
    jobLocation: candidate.jobLocation || summary.jobLocation,
    jobType: candidate.jobType || summary.jobType,
  };
};

const mapStageToStatus = (stageId) => {
  switch (stageId) {
    case 'offer':
      return 'offered';
    default:
      return stageId;
  }
};

const extractCandidatesFromResponse = (result) => {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data?.candidates)) return result.data.candidates;
  if (Array.isArray(result?.data?.data)) return result.data.data;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.candidates)) return result.candidates;
  return [];
};

const AddCandidateForm = ({
  setShowAddCandidate,
  formData,
  setFormData,
  handleInputChange,
  handleFileChange,
  handleSubmit,
  jobOptions,
  selectedCandidate
}) => {
  // Prefill data if editing a candidate
  useEffect(() => {
    if (selectedCandidate) {
      // Only prefill if we're editing an existing candidate
      setFormData({
        name: selectedCandidate.name || "",
        email: selectedCandidate.email || "",
        phone: selectedCandidate.phone || "",
        jobId: selectedCandidate.jobId || "",
        experience: selectedCandidate.experience || "",
        location: selectedCandidate.location || "",
        skills: selectedCandidate.skills?.join(', ') || "",
        education: selectedCandidate.education || "",
        currentCompany: selectedCandidate.currentCompany || "",
        expectedSalary: selectedCandidate.expectedSalary || "",
        notes: selectedCandidate.notes || "",
        resume: null, // resume file won't prefill
      });
    } else {
      // Reset form for new candidate
      setFormData({
        name: '',
        email: '',
        phone: '',
        jobId: '',
        experience: '',
        location: '',
        skills: '',
        education: '',
        currentCompany: '',
        expectedSalary: '',
        notes: '',
        resume: null,
      });
    }
  }, [selectedCandidate, setFormData]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedCandidate ? "Edit Candidate" : "Add New Candidate"}
            </h2>
            <button
              onClick={() => setShowAddCandidate(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Position *
                </label>
                <select
                  name="jobId"
                  value={formData.jobId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand max-w-full"
                  style={{ maxWidth: '100%' }}
                  required
                >
                  <option value="">Select Job</option>
                  {jobOptions.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title} ({job.status})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience
                </label>
                <input
                  type="text"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  placeholder="e.g., 3 years"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  placeholder="e.g., Mumbai, India"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Skills (comma-separated)
              </label>
              <input
                type="text"
                name="skills"
                value={formData.skills}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                placeholder="e.g., React.js, Node.js, MongoDB"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Education
              </label>
              <input
                type="text"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                placeholder="e.g., B.Tech Computer Science"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Company
              </label>
              <input
                type="text"
                name="currentCompany"
                value={formData.currentCompany}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                placeholder="e.g., TechCorp Pvt Ltd"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Salary
              </label>
              <input
                type="text"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                placeholder="e.g., 8-12 LPA"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                placeholder="Additional notes about the candidate..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resume
              </label>

              {/* Show current resume if editing */}
              {selectedCandidate && selectedCandidate.resume && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">
                        Current Resume: {
                          selectedCandidate.resume?.originalName ||
                          selectedCandidate.resume?.filename ||
                          selectedCandidate.resume?.name ||
                          'Resume.pdf'
                        }
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          let fileUrl;
                          if (selectedCandidate.resume?.url) {
                            fileUrl = selectedCandidate.resume.url;
                          } else if (typeof selectedCandidate.resume === "string") {
                            fileUrl = selectedCandidate.resume;
                          } else {
                            toast.error('Document not found', { icon: '⚠️' });
                            return;
                          }

                          const link = document.createElement('a');
                          link.href = fileUrl;
                          link.target = '_blank';
                          link.download = selectedCandidate.resume?.filename || 'resume.pdf';

                          // Ensure we have a full URL
                          if (!fileUrl.startsWith('http')) {
                            const baseUrl = API_BASE_URL.replace('/api', '');
                            link.href = `${baseUrl}${fileUrl}`;
                          }

                          // Add authentication token to the URL for resume access
                          const token = localStorage.getItem('auth_token');
                          if (token) {
                            const separator = link.href.includes('?') ? '&' : '?';
                            link.href = `${link.href}${separator}token=${encodeURIComponent(token)}`;
                          }

                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } catch (error) {
                          toast.error('Error downloading resume: ' + error.message);
                        }
                      }}
                      className="text-[#0F2942] hover:text-blue-800 text-sm"
                    >
                      View Current Resume
                    </button>
                  </div>
                </div>
              )}

              <input
                type="file"
                name="resume"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedCandidate && selectedCandidate.resume
                  ? "Select a new file to replace the current resume"
                  : "Upload resume file (PDF, DOC, DOCX)"
                }
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAddCandidate(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {selectedCandidate ? "Update Candidate" : "Add Candidate"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const CandidatePipeline = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── URL-persisted filter state ──────────────────────────────────────────────
  const defaultPipelineFilters = useMemo(() => ({
    search: '',
    filterJob: 'all',
    filterStartDate: '',
    filterEndDate: '',
    viewMode: 'pipeline',
    selectedListStage: 'applied',
    listViewPage: 1,
    sortBy: 'appliedDate',
    sortOrder: 'desc',
  }), []);

  const {
    filters: pipelineFilters,
    searchTerm: urlSearchTerm,
    setSearchTerm: setUrlSearchTerm,
    updateFilter: updatePipelineFilter,
    updatePage: updatePipelineListPage,
    updateSort: updatePipelineSort,
    resetFilters: resetPipelineFilters,
  } = useUrlTableState(defaultPipelineFilters, { searchKey: 'search' });

  // Derive individual values from URL state
  const filterJob = pipelineFilters.filterJob || 'all';
  const filterStartDate = pipelineFilters.filterStartDate || '';
  const filterEndDate = pipelineFilters.filterEndDate || '';
  // ────────────────────────────────────────────────────────────────────────────

  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(() => pipelineFilters.search || '');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [alertShown, setAlertShown] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState(null);
  const [showScreeningConfirm, setShowScreeningConfirm] = useState(false);
  const [candidateToScreening, setCandidateToScreening] = useState(null);
  const [screeningTargetStage, setScreeningTargetStage] = useState(null);
  const [showShortlistedConfirm, setShowShortlistedConfirm] = useState(false);
  const [candidateToShortlisted, setCandidateToShortlisted] = useState(null);
  const [shortlistedTargetStage, setShortlistedTargetStage] = useState(null);
  const [showQuickMoveModal, setShowQuickMoveModal] = useState(false);
  const [candidateForQuickMove, setCandidateForQuickMove] = useState(null);
  const [showQuickMoveConfirm, setShowQuickMoveConfirm] = useState(false);
  const [quickMoveTargetStage, setQuickMoveTargetStage] = useState(null);
  // Reason states for each move modal
  const [rejectReason, setRejectReason] = useState('');
  const [screeningReason, setScreeningReason] = useState('');
  const [shortlistedReason, setShortlistedReason] = useState('');
  const [quickMoveReason, setQuickMoveReason] = useState('');
  const [interviewWarning, setInterviewWarning] = useState({
    show: false,
    message: '',
  });
  const [viewMode, setViewModeState] = useState(() => pipelineFilters.viewMode || 'pipeline');
  const setViewMode = useCallback((mode) => {
    setViewModeState(mode);
    updatePipelineFilter({ viewMode: mode });
  }, [updatePipelineFilter]);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Schedule Interview Modal States
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [candidateForInterview, setCandidateForInterview] = useState(null);
  const [interviewFormData, setInterviewFormData] = useState({
    candidateId: "",
    interviewerId: "",
    date: "",
    time: "",
    duration: 60,
    type: "video",
    location: "",
    meetingLink: "",
    notes: "",
    // round: "", // Commented out - Interview Round field removed
  });
  const [interviewErrors, setInterviewErrors] = useState({});
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewerSearchTerm, setInterviewerSearchTerm] = useState('');
  const [debouncedInterviewerSearch, setDebouncedInterviewerSearch] = useState('');
  const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
  const [selectedInterviewerData, setSelectedInterviewerData] = useState(null);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [offerCandidate, setOfferCandidate] = useState(null);
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [sendingOfferId, setSendingOfferId] = useState(null);
  const [showResendOfferModal, setShowResendOfferModal] = useState(false);
  const [offerToResend, setOfferToResend] = useState(null);
  const [defaultInterviewers, setDefaultInterviewers] = useState([]);
  const [isLoadingDefaultInterviewers, setIsLoadingDefaultInterviewers] = useState(false);
  const candidateDropdownRef = useRef(null);
  const interviewerDropdownRef = useRef(null);

  // Export & Selection State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Sorting State (URL-persisted)
  const sortColumn = pipelineFilters.sortBy || 'appliedDate';
  const sortDirection = pipelineFilters.sortOrder || 'desc';

  const handleSort = (column) => {
    const newDirection = sortColumn === column
      ? (sortDirection === 'asc' ? 'desc' : 'asc')
      : 'asc';
    updatePipelineSort(column, newDirection);
    setListViewPage(1);
  };

  const makeCandidateKey = useCallback((candidate, index = 0) => {
    const baseId = candidate?.candidateId || candidate?.id || 'CAND';
    return `${baseId}${index + 1}`;
  }, []);


  // Debounced search value for React Query — driven by the URL hook's built-in debounce
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(pipelineFilters.search || '');

  // Sync local input value from URL on mount / browser back-forward
  useEffect(() => {
    setSearchInputValue(urlSearchTerm || '');
  }, [urlSearchTerm]);

  // Keep debouncedSearchValue in sync with the URL param (hook debounces writes)
  useEffect(() => {
    setDebouncedSearchValue(pipelineFilters.search || '');
  }, [pipelineFilters.search]);

  // Debounce interviewer search for Schedule Interview
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInterviewerSearch(interviewerSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [interviewerSearchTerm]);

  // viewMode is persisted via URL; no localStorage sync needed

  const loadOfferTemplates = useCallback(() => {
    const mockTemplates = [
      {
        id: 'standard',
        name: 'Standard Offer',
        department: 'All',
        salaryRange: '500000-1500000',
        benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus'],
        content: 'We are pleased to offer you the position of {jobTitle}...',
        isActive: true,
      },
      {
        id: 'engineering',
        name: 'Engineering Offer',
        department: 'Engineering',
        salaryRange: '800000-1500000',
        benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus', 'Professional Development'],
        content: 'We are pleased to offer you the position of {jobTitle}...',
        isActive: true,
      },
      {
        id: 'hr',
        name: 'HR Offer',
        department: 'Human Resources',
        salaryRange: '600000-1200000',
        benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus', 'Professional Development'],
        content: 'We are pleased to offer you the position of {jobTitle}...',
        isActive: true,
      },
    ];
    setOfferTemplates(mockTemplates);
  }, []);

  useEffect(() => {
    loadOfferTemplates();
  }, [loadOfferTemplates]);

  // PDF Viewer Functions
  const openPdfViewer = async (resumeData) => {
    try {
      setPdfLoading(true);
      setPdfError('');


      if (!resumeData) {
        throw new Error('No resume data available');
      }

      let fileUrl;

      // Check if resume has a proper URL (Cloudinary or local)
      if (resumeData?.url) {
        fileUrl = resumeData.url;

        // For Cloudinary URLs, use original URL directly for now
        if (resumeData.url.includes('cloudinary.com')) {
          fileUrl = resumeData.url;
        } else {
          // For local URLs, construct the full URL
          const baseUrl = API_BASE_URL;
          fileUrl = `${baseUrl}${resumeData.url}`;
        }
      } else if (typeof resumeData === "string") {
        fileUrl = resumeData;
      } else if (resumeData instanceof File) {
        fileUrl = URL.createObjectURL(resumeData);
      } else if (resumeData?.filename) {
        // For local files, construct URL from filename
        const candidateId = selectedCandidate?._id || selectedCandidate?.id;
        if (candidateId) {
          const baseUrl = API_BASE_URL;
          fileUrl = `${baseUrl}/recruitment/candidates/${candidateId}/resume`;
        } else {
          throw new Error('Candidate ID not available for resume URL construction');
        }
      } else {
        throw new Error('Resume format not supported or URL not available');
      }

      if (fileUrl) {
        // For local URLs, ensure we have a full URL and add auth token
        if (!fileUrl.startsWith('http')) {
          const baseUrl = API_BASE_URL.replace('/api', '');
          fileUrl = `${baseUrl}${fileUrl}`;
        }

        // Add authentication token for local files only
        if (!fileUrl.includes('cloudinary.com')) {
          const authToken = localStorage.getItem('auth_token');
          if (authToken) {
            const separator = fileUrl.includes('?') ? '&' : '?';
            fileUrl = `${fileUrl}${separator}token=${encodeURIComponent(authToken)}`;
          }
        }

        setPdfUrl(fileUrl);
        setShowPdfViewer(true);
      }
    } catch (error) {
      setPdfError(error.message);
      toast.error('Error opening resume: ' + error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    setShowPdfViewer(false);
    setPdfUrl('');
    setPdfError('');
  };

  // Role-based access control functions
  const canExportData = () => {
    return user?.role === 'hr_manager' || user?.role === 'super_admin';
  };

  const canImportData = () => {
    return user?.role === 'hr_manager' || user?.role === 'super_admin' || user?.role === 'recruiter';
  };

  const canAddCandidate = () => {
    return user?.role === 'hr_manager' || user?.role === 'super_admin' || user?.role === 'recruiter';
  };

  // React Query: Export candidates mutation
  const exportCandidatesMutation = useMutation({
    mutationFn: async (filters) => {
      const blobData = await candidateService.exportCandidates(filters);

      if (!blobData || blobData.size === 0) {
        throw new Error('No data received from export API');
      }

      // Create download link
      const blob = new Blob([blobData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `candidate_pipeline_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return blobData;
    },
    onSuccess: () => {
      toast.success('Candidates exported successfully!');
    },
    onError: (error) => {

      let errorMessage = 'Error exporting candidates. Please try again.';
      if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    },
  });

  // Handler functions
  const handleExport = async () => {
    const filters = {
      jobId: filterJob !== 'all' ? filterJob : undefined,
      search: searchTerm || undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined
    };

    exportCandidatesMutation.mutate(filters);
  };

  // React Query: Import candidates mutation
  const importCandidatesMutation = useMutation({
    mutationFn: async (formData) => {
      toast('Processing import file...', { icon: 'ℹ️' });
      const response = await candidateService.importCandidates(formData);
      return response;
    },
    onSuccess: (response) => {
      if (response.success || response.responseCode === 200) {
        const importedCount = response.data?.importedCount || 0;
        const errorCount = response.data?.errorCount || 0;

        if (importedCount > 0) {
          toast.success(`Successfully imported ${importedCount} candidates! ${errorCount > 0 ? `${errorCount} rows had errors.` : ''}`);
          triggerPipelineRefresh();
        } else {
          toast.error('No candidates were imported. Please check your file format and data.');
        }

        // Show detailed errors if any
        if (response.data?.errors && response.data.errors.length > 0) {
        }
      } else {
        toast.error(response.responseMessage || response.message || 'Import failed. Please check your file format.');
      }
    },
    onError: (error) => {

      let errorMessage = 'Error importing candidates. Please try again.';

      if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        errorMessage = 'Import failed. Please check your file format and try again.';
      }

      toast.error(errorMessage);
    },
  });

  const handleImport = () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv,.xlsx,.xls';
    fileInput.style.display = 'none';

    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      importCandidatesMutation.mutate(formData);

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };


  // React Query: Fetch jobs
  const {
    data: jobOptionsData
  } = useQuery({
    queryKey: ['activeJobs'],
    queryFn: async () => {
      const result = await jobService.getJobs({ page: 1, limit: 1000 });

      if (result?.responseCode === 200 || result?.success || result?.data) {
        const jobsData = Array.isArray(result?.data?.jobs)
          ? result.data.jobs
          : Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result)
              ? result
              : [];

        // Filter only active jobs (published and active status)
        const activeJobs = jobsData.filter(job =>
          job.status === 'published' || job.status === 'active'
        );

        const transformedJobs = activeJobs.map(job => ({
          id: job.id || job._id,
          _id: job._id || job.id,
          title: job.title,
          status: job.status,
          department: job.department || job.departmentName || job?.jobDetails?.department || 'N/A',
          location: job.location || job.workLocation || job?.jobDetails?.location || '',
          employmentType: job.employmentType || job.type,
          salaryStructureId: job.salaryStructureId || job.salaryStructure?._id || job.salaryStructure?.structureId,
          salaryStructure: job.salaryStructure,
          offerType: job.offerType || job.employmentType || job.type,
        }));

        return transformedJobs;
      }
      return [];
    },
    staleTime: 0,
    retry: 2,
    onError: (err) => {
    },
  });

  const jobOptions = jobOptionsData || [];

  const { data: offerSalaryStructuresData, isLoading: loadingOfferSalaryStructures } = useQuery({
    queryKey: ['salaryStructures', 'active', 'candidate-pipeline'],
    queryFn: async () => {
      try {
        const result = await getSalaryStructures({ status: 'active', limit: 200 });
        let structures = [];
        if (Array.isArray(result?.data?.structures)) {
          structures = result.data.structures;
        } else if (Array.isArray(result?.data?.data)) {
          structures = result.data.data;
        } else if (Array.isArray(result?.data)) {
          structures = result.data;
        } else if (Array.isArray(result?.structures)) {
          structures = result.structures;
        } else if (Array.isArray(result)) {
          structures = result;
        }
        return structures.filter((structure) => (structure.status || '').toLowerCase() === 'active');
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    retry: 2,
  });
  const offerSalaryStructures = offerSalaryStructuresData || [];

  const { data: offerShiftsData, isLoading: loadingOfferShifts } = useQuery({
    queryKey: ['shifts', 'candidate-pipeline'],
    queryFn: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/shifts`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          let shifts = [];
          if (Array.isArray(result?.data?.shifts)) {
            shifts = result.data.shifts;
          } else if (Array.isArray(result?.data)) {
            shifts = result.data;
          } else if (Array.isArray(result?.shifts)) {
            shifts = result.shifts;
          } else if (Array.isArray(result)) {
            shifts = result;
          }

          return shifts.filter(
            (shift) =>
              String(shift.isActive ?? shift.status)?.toLowerCase() === 'active' ||
              shift.isActive === true
          );
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    retry: 2,
  });
  const offerShifts = offerShiftsData || [];

  // React Query: Search Employees for Interviewer (only fetch on search, no initial load)
  const { data: searchedInterviewersData, isLoading: isLoadingInterviewerSearch } = useQuery({
    queryKey: ['schedule-interview-employees-search', debouncedInterviewerSearch],
    queryFn: async () => {
      if (!debouncedInterviewerSearch || !debouncedInterviewerSearch.trim()) {
        return [];
      }

      const baseUrl = API_BASE_URL;
      const url = `${baseUrl}/hr/employees?search=${encodeURIComponent(debouncedInterviewerSearch.trim())}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200 && data.data && data.data.employees) {
          return data.data.employees || [];
        } else if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (Array.isArray(data)) {
          return data;
        } else if (data.employees && Array.isArray(data.employees)) {
          return data.employees;
        }
      }
      return [];
    },
    enabled: !!debouncedInterviewerSearch && debouncedInterviewerSearch.trim().length > 0,
    staleTime: 0,
  });

  const loadDefaultInterviewers = useCallback(async () => {
    setIsLoadingDefaultInterviewers(true);
    try {
      const queryString = new URLSearchParams({
        page: 1,
        limit: 20,
        status: 'active',
      }).toString();

      const response = await hrService.getEmployees(queryString);
      let employees = [];

      if (response?.success) {
        const payload = response.data;
        if (Array.isArray(payload?.employees)) {
          employees = payload.employees;
        } else if (Array.isArray(payload?.data?.employees)) {
          employees = payload.data.employees;
        } else if (Array.isArray(payload?.data)) {
          employees = payload.data;
        } else if (Array.isArray(payload)) {
          employees = payload;
        }
      } else if (Array.isArray(response?.data?.employees)) {
        employees = response.data.employees;
      } else if (Array.isArray(response?.data)) {
        employees = response.data;
      }

      setDefaultInterviewers(employees || []);
    } catch (error) {
      setDefaultInterviewers([]);
    } finally {
      setIsLoadingDefaultInterviewers(false);
    }
  }, []);

  useEffect(() => {
    if (showScheduleInterview) {
      loadDefaultInterviewers();
    }
  }, [showScheduleInterview, loadDefaultInterviewers]);

  const isSearchingInterviewers = !!debouncedInterviewerSearch.trim();
  const dropdownInterviewers = isSearchingInterviewers ? (searchedInterviewersData || []) : defaultInterviewers;
  const isDropdownLoading = isSearchingInterviewers ? isLoadingInterviewerSearch : isLoadingDefaultInterviewers;

  // Fetch locations using React Query
  const { data: locationsData, isLoading: loadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      try {
        const result = await locationService.getLocations();
        if (result.success && result.data) {
          if (result.data.data?.locations && Array.isArray(result.data.data.locations)) {
            return result.data.data.locations;
          }
          if (result.data.locations && Array.isArray(result.data.locations)) {
            return result.data.locations;
          }
          if (Array.isArray(result.data)) {
            return result.data;
          }
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    retry: 2,
    enabled: showScheduleInterview, // Only fetch when modal is open
  });

  const locations = locationsData || [];

  const { data: offerLocationsData, isLoading: loadingOfferLocations } = useQuery({
    queryKey: ['offer-locations', 'candidate-pipeline'],
    queryFn: async () => {
      try {
        const result = await locationService.getActiveLocations();
        if (result.success) {
          return result.data.map((loc) => loc.name || loc.label || loc.value);
        }
        return ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Remote'];
      } catch (error) {
        return ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Remote'];
      }
    },
    staleTime: 0,
    retry: 2,
  });
  const offerLocations = offerLocationsData || ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Remote'];

  // Pre-fill candidate when modal opens
  useEffect(() => {
    if (showScheduleInterview && candidateForInterview) {
      setInterviewFormData(prev => ({
        ...prev,
        candidateId: candidateForInterview.id,
      }));
      const candidateName = candidateForInterview.name ||
        `${candidateForInterview.firstName || ''} ${candidateForInterview.lastName || ''}`.trim() ||
        'Unknown';
      setCandidateSearchTerm(candidateName);
    }
  }, [showScheduleInterview, candidateForInterview]);

  // Close candidate/interviewer dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        if (showCandidateDropdown && candidateDropdownRef.current && !candidateDropdownRef.current.contains(event.target)) {
          setShowCandidateDropdown(false);
        }
        if (showInterviewerDropdown && interviewerDropdownRef.current && !interviewerDropdownRef.current.contains(event.target)) {
          setShowInterviewerDropdown(false);
        }
      } catch (_) { }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCandidateDropdown, showInterviewerDropdown]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobId: '',
    experience: '',
    location: '',
    skills: '',
    education: '',
    currentCompany: '',
    expectedSalary: '',
    notes: '',
    resume: null
  });


  const stages = useMemo(
    () => [
      { id: 'applied', name: 'Applied', color: 'bg-blue-100 text-blue-800' },
      { id: 'screening', name: 'Screening', color: 'bg-cyan-100 text-cyan-800' },
      { id: 'shortlisted', name: 'Shortlisted', color: 'bg-yellow-100 text-yellow-800' },
      { id: 'interview', name: 'Scheduled Interview', color: 'bg-indigo-100 text-indigo-800' },
      { id: 'interviewed', name: 'Interviewed', color: 'bg-purple-100 text-purple-800' },
      { id: 'documentation', name: 'Documentation', color: 'bg-teal-100 text-teal-800' },
      { id: 'offer', name: 'Offer', color: 'bg-green-100 text-green-800' },
      { id: 'hired', name: 'Hired', color: 'bg-emerald-100 text-emerald-800' },
      { id: 'rejected', name: 'Rejected', color: 'bg-red-100 text-red-800' },
      { id: 'backout', name: 'Backout', color: 'bg-amber-100 text-amber-800' }
    ],
    []
  );

  const createEmptyStageState = useCallback(() => {
    const state = {};
    stages.forEach(stage => {
      state[stage.id] = {
        items: [],
        page: 0,
        hasMore: true,
        loading: false,
        error: null,
      };
    });
    return state;
  }, [stages]);

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineRefreshToken, setPipelineRefreshToken] = useState(0);
  const [stageData, setStageData] = useState(() => createEmptyStageState());
  const [selectedListStage, setSelectedListStageState] = useState(() => pipelineFilters.selectedListStage || stages[0]?.id || 'applied');

  const setSelectedListStage = useCallback((stage) => {
    setSelectedListStageState(stage);
    updatePipelineFilter({ selectedListStage: stage, listViewPage: 1 });
  }, [updatePipelineFilter]);

  const [listViewPage, setListViewPageState] = useState(() => Number(pipelineFilters.listViewPage) || 1);

  const setListViewPage = useCallback((page) => {
    setListViewPageState(page);
    updatePipelineListPage(page);
    // Mirror into listViewPage key too
    updatePipelineFilter({ listViewPage: page });
  }, [updatePipelineFilter, updatePipelineListPage]);

  useEffect(() => {
    setListViewPage(1);
  }, [selectedListStage, filterJob, debouncedSearchValue, filterStartDate, filterEndDate]);

  // selectedListStage is persisted via URL; no localStorage sync needed

  const [stageVisibleCounts, setStageVisibleCounts] = useState(() => {
    const initialCounts = {};
    stages.forEach(stage => {
      initialCounts[stage.id] = PIPELINE_BATCH_SIZE;
    });
    return initialCounts;
  });

  useEffect(() => {
    const resetCounts = {};
    stages.forEach(stage => {
      resetCounts[stage.id] = PIPELINE_BATCH_SIZE;
    });
    setStageVisibleCounts(resetCounts);
  }, [stages, filterJob, debouncedSearchValue, pipelineRefreshToken]);

  // Auto-focus search bar on component mount and after loading completes
  useEffect(() => {
    if (!loading && searchInputRef.current) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [loading]);

  const loadStagePage = useCallback(
    async (stageId, pageToLoad = 1) => {
      setStageData(prev => ({
        ...prev,
        [stageId]: {
          ...(prev[stageId] || {}),
          loading: true,
          error: null,
        },
      }));

      try {
        const params = {
          page: pageToLoad,
          limit: PIPELINE_BATCH_SIZE,
          status: mapStageToStatus(stageId),
          fields: PIPELINE_FIELDS_PARAM,
          view: 'pipeline',
        };
        if (filterJob !== 'all') {
          params.jobId = filterJob;
        }
        if (debouncedSearchValue) {
          params.search = debouncedSearchValue;
        }
        if (filterStartDate) {
          params.startDate = filterStartDate;
        }
        if (filterEndDate) {
          params.endDate = filterEndDate;
        }

        const result = await candidateService.getCandidates(params);
        const fetchedCandidates = extractCandidatesFromResponse(result).map(mapCandidateSummary);
        const normalizedCandidates = fetchedCandidates.map((candidate) => ({
          ...candidate,
          stage: candidate.stage || stageId,
          status: candidate.status || mapStageToStatus(stageId),
        }));
        const pagination = result?.data?.pagination || result?.pagination;
        const hasMore = pagination?.hasNext ?? (fetchedCandidates.length === PIPELINE_BATCH_SIZE);

        setStageData(prev => ({
          ...prev,
          [stageId]: {
            ...(prev[stageId] || {}),
            items:
              pageToLoad === 1
                ? normalizedCandidates
                : [...(prev[stageId]?.items || []), ...normalizedCandidates],
            page: pageToLoad,
            hasMore,
            loading: false,
            error: null,
          },
        }));

        setStageVisibleCounts(prev => ({
          ...prev,
          [stageId]:
            pageToLoad === 1
              ? Math.min(PIPELINE_BATCH_SIZE, fetchedCandidates.length || PIPELINE_BATCH_SIZE)
              : (prev[stageId] || PIPELINE_BATCH_SIZE) + PIPELINE_BATCH_SIZE,
        }));

        setCandidates(prev => {
          const map = new Map(prev.map(item => [item.id, item]));
          normalizedCandidates.forEach(candidate => {
            const existing = map.get(candidate.id);
            map.set(candidate.id, existing ? { ...existing, ...candidate } : candidate);
          });
          return Array.from(map.values());
        });
      } catch (err) {
        console.error('💥 Error loading stage', stageId, err);
        setStageData(prev => ({
          ...prev,
          [stageId]: {
            ...(prev[stageId] || {}),
            loading: false,
            error: err.message || 'Failed to load candidates',
          },
        }));
        throw err;
      }
    },
    [filterJob, debouncedSearchValue, filterStartDate, filterEndDate]
  );

  useEffect(() => {
    let isMounted = true;
    const resetState = createEmptyStageState();
    setStageData(resetState);
    setCandidates([]);
    setLoading(true);
    setError(null);

    const fetchInitial = async () => {
      try {
        await Promise.all(stages.map(stage => loadStagePage(stage.id, 1)));
      } catch (err) {
        if (isMounted) {
          console.error('💥 Error loading pipeline data:', err);
          setError(err.message || 'Failed to load candidates');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchInitial();

    return () => {
      isMounted = false;
    };
  }, [stages, filterJob, debouncedSearchValue, filterStartDate, filterEndDate, loadStagePage, createEmptyStageState, pipelineRefreshToken]);

  const triggerPipelineRefresh = useCallback(() => {
    setPipelineRefreshToken(prev => prev + 1);
  }, []);

  const {
    data: stageCountsResult,
    isFetching: isFetchingStageCounts,
  } = useQuery({
    queryKey: ['candidate-stage-counts', filterJob, debouncedSearchValue, filterStartDate, filterEndDate, pipelineRefreshToken],
    queryFn: async () => {
      const params = {};
      if (filterJob !== 'all') {
        params.jobId = filterJob;
      }
      if (debouncedSearchValue) {
        params.search = debouncedSearchValue;
      }
      if (filterStartDate) {
        params.startDate = filterStartDate;
      }
      if (filterEndDate) {
        params.endDate = filterEndDate;
      }

      const response = await candidateService.getStageCounts(params);
      return response?.data || response;
    },
    staleTime: 0,
    keepPreviousData: true,
  });

  const stageCounts = stageCountsResult || {};

  const {
    data: listViewResponse,
    isFetching: isFetchingListView,
    isError: listViewError,
    error: listViewErrorObj,
  } = useQuery({
    queryKey: [
      'candidate-list-view',
      selectedListStage,
      listViewPage,
      filterJob,
      debouncedSearchValue,
      filterStartDate,
      filterEndDate,
      pipelineRefreshToken,
      sortColumn,
      sortDirection,
    ],
    queryFn: async () => {
      const params = {
        page: listViewPage,
        limit: LIST_VIEW_PAGE_SIZE,
        status: mapStageToStatus(selectedListStage),
        fields: PIPELINE_FIELDS_PARAM,
        view: 'list',
        sortBy: sortColumn,
        sortOrder: sortDirection,
      };

      if (filterJob !== 'all') {
        params.jobId = filterJob;
      }
      if (debouncedSearchValue) {
        params.search = debouncedSearchValue;
      }
      if (filterStartDate) {
        params.startDate = filterStartDate;
      }
      if (filterEndDate) {
        params.endDate = filterEndDate;
      }

      const response = await candidateService.getCandidates(params);
      return response;
    },
    enabled: viewMode === 'list',
    keepPreviousData: true,
  });

  const listViewCandidates = useMemo(() => {
    if (!listViewResponse) return [];
    const summarized = extractCandidatesFromResponse(listViewResponse).map(mapCandidateSummary);
    return summarized.map((candidate) => ({
      ...candidate,
      stage: candidate.stage || selectedListStage,
      status: candidate.status || mapStageToStatus(selectedListStage),
    }));
  }, [listViewResponse, selectedListStage]);

  const paginationFromResponse =
    listViewResponse?.data?.pagination || listViewResponse?.pagination;

  const listViewErrorMessage = listViewError
    ? listViewErrorObj?.message || 'Failed to load candidates'
    : null;

  const totalListCandidates =
    paginationFromResponse?.totalCandidates ??
    stageCounts?.[selectedListStage] ??
    listViewCandidates.length;

  const listViewTotalPages =
    paginationFromResponse?.totalPages ??
    Math.max(1, Math.ceil((totalListCandidates || 0) / LIST_VIEW_PAGE_SIZE) || 1);

  const safeListViewPage = Math.min(
    Math.max(listViewPage || 1, 1),
    listViewTotalPages
  );

  const isListPageTransitioning =
    isFetchingListView &&
    typeof paginationFromResponse?.currentPage === 'number' &&
    paginationFromResponse.currentPage !== listViewPage;

  const listViewShowingFrom =
    totalListCandidates === 0 ? 0 : (safeListViewPage - 1) * LIST_VIEW_PAGE_SIZE + 1;
  const listViewShowingTo =
    totalListCandidates === 0
      ? 0
      : Math.min(listViewShowingFrom + listViewCandidates.length - 1, totalListCandidates);

  const handleListViewPageChange = useCallback(
    (page) => {
      if (page < 1 || page > listViewTotalPages) return;
      setListViewPage(page);
    },
    [listViewTotalPages]
  );

  useEffect(() => {
    if (viewMode !== 'list') return;
    if (!isFetchingListView && paginationFromResponse?.currentPage) {
      const serverPage = paginationFromResponse.currentPage;
      if (serverPage !== listViewPage) {
        setListViewPage(serverPage);
      }
    }
  }, [viewMode, isFetchingListView, paginationFromResponse?.currentPage, listViewPage]);

  // Interview scheduling constants
  const interviewTypes = [
    { id: "video", name: "Video Call", icon: Video },
    { id: "in_person", name: "In Person", icon: MapPin },
  ];

  const interviewRounds = [
    "Initial Screening",
    "Technical Round 1",
    "Technical Round 2",
    "HR Round",
    "Manager Round",
    "Final Round",
  ];

  // Helper function to filter input
  const filterAlphanumericSpaceComma = (value, maxLength = Infinity) => {
    const filtered = value.replace(/[^a-zA-Z0-9\s,]/g, '');
    return maxLength !== Infinity ? filtered.slice(0, maxLength) : filtered;
  };

  // Interview form handlers - Memoized with useCallback to prevent re-creation
  const handleInterviewInputChange = useCallback((e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Date validation - prevent past dates
    if (name === 'date' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        setInterviewErrors(prev => ({ ...prev, date: 'Interview date cannot be in the past.' }));
        return;
      }
    }
    // Filter notes to only alphanumeric, space, and comma with character limit
    if (name === 'notes') {
      const MAX_NOTES = 1000;
      value = filterAlphanumericSpaceComma(value, MAX_NOTES);
    }

    // Enforce character limit for meetingLink
    if (name === 'meetingLink') {
      if (value.length > 300) {
        value = value.slice(0, 300);
      }
    }

    // Clear location/meetingLink when interview type changes
    const updateData = { [name]: value };
    const errorUpdates = { [name]: undefined };

    if (name === 'type') {
      if (value === 'video') {
        updateData.location = '';
        errorUpdates.location = undefined;
      } else if (value === 'in_person') {
        updateData.meetingLink = '';
        errorUpdates.meetingLink = undefined;
      }
    }

    setInterviewFormData(prev => ({
      ...prev,
      ...updateData,
    }));

    setInterviewErrors(prev => ({ ...prev, ...errorUpdates }));
  }, []);

  const validateInterviewField = useCallback((name, value) => {
    let msg = '';
    const allowedDurations = [30, 45, 60, 90, 120];
    const isUrl = (u) => {
      try { new URL(u); return true; } catch { return false; }
    };

    switch (name) {
      case 'candidateId':
        if (!value) msg = 'Candidate is required.';
        break;
      case 'interviewerId':
        if (!value) msg = 'Interviewer is required.';
        break;
      case 'date': {
        if (!value) { msg = 'Date is required.'; break; }
        const d = new Date(value); const today = new Date(); today.setHours(0, 0, 0, 0);
        if (d < today) msg = 'Interview date cannot be in the past.';
        break;
      }
      case 'time':
        if (!value) msg = 'Time is required.';
        break;
      case 'duration': {
        const dur = parseInt(value, 10);
        if (!allowedDurations.includes(dur)) msg = 'Invalid duration selected.';
        break;
      }
      case 'type':
        if (!value) msg = 'Interview type is required.';
        break;
      case 'meetingLink':
        if (interviewFormData.type === 'video') {
          if (!value) msg = 'Meeting link is required for video interviews.';
          else if (value.length > 300) msg = 'Meeting link is too long (max 300 chars).';
          else if (!isUrl(value)) msg = 'Please enter a valid URL.';
        }
        break;
      case 'location':
        if (interviewFormData.type === 'in_person') {
          if (!value || value.trim().length < 2) msg = 'Location is required for in-person interviews.';
        }
        break;
      case 'notes': {
        if (value && value.length > 1000) msg = 'Notes must be ≤ 1000 characters.';
        break;
      }
      default: break;
    }

    setInterviewErrors(prev => ({ ...prev, [name]: msg || undefined }));
  }, [interviewFormData.type]);

  const validateInterviewForm = () => {
    const fieldsToValidate = ['candidateId', 'interviewerId', 'date', 'time', 'duration', 'type', 'location', 'meetingLink', /* 'round', */ 'notes'];
    fieldsToValidate.forEach(f => validateInterviewField(f, interviewFormData[f]));
    const nextErrors = {};
    fieldsToValidate.forEach((f) => {
      let v = interviewFormData[f];
      let msg = '';
      const allowedDurations = [30, 45, 60, 90, 120];
      const isUrl = (u) => { try { new URL(u); return true; } catch { return false; } };
      switch (f) {
        case 'candidateId': if (!v) msg = 'Candidate is required.'; break;
        case 'interviewerId': if (!v) msg = 'Interviewer is required.'; break;
        case 'date': {
          if (!v) { msg = 'Date is required.'; break; }
          const d = new Date(v); const today = new Date(); today.setHours(0, 0, 0, 0); if (d < today) msg = 'Interview date cannot be in the past.'; break;
        }
        case 'time': if (!v) msg = 'Time is required.'; break;
        case 'duration': { const dur = parseInt(v, 10); if (!allowedDurations.includes(dur)) msg = 'Invalid duration selected.'; break; }
        case 'type': if (!v) msg = 'Interview type is required.'; break;
        case 'meetingLink': if (interviewFormData.type === 'video') { if (!v) msg = 'Meeting link is required for video interviews.'; else if (v.length > 300) msg = 'Meeting link is too long (max 300 chars).'; else if (!isUrl(v)) msg = 'Please enter a valid URL.'; } break;
        case 'location': if (interviewFormData.type === 'in_person') { if (!v || String(v).trim().length < 2) msg = 'Location is required for in-person interviews.'; } break;
        // case 'round': if (!v || String(v).trim() === '') msg='Interview round is required.'; else if (!interviewRounds.includes(v)) msg='Invalid round selected.'; break; // Commented out - Interview Round field removed
        case 'notes': {
          if (v && String(v).length > 1000) msg = 'Notes must be ≤ 1000 characters.';
          break;
        }
        default: break;
      }
      if (msg) nextErrors[f] = msg;
    });
    setInterviewErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleInterviewSubmit = async (e) => {
    e.preventDefault();

    if (interviewLoading) {
      return;
    }

    setInterviewLoading(true);

    const ok = validateInterviewForm();
    if (!ok) {
      setInterviewLoading(false);
      return;
    }

    try {
      const candidate = candidateForInterview || candidates.find((c) => c.id === interviewFormData.candidateId);
      const interviewer = selectedInterviewerData;

      if (!candidate) {
        throw new Error("Please select a candidate");
      }

      if (!interviewer || !interviewFormData.interviewerId) {
        throw new Error("Please select an interviewer");
      }

      if (!interviewFormData.date) {
        throw new Error('Please select an interview date');
      }

      if (!interviewFormData.time) {
        throw new Error('Please select an interview time');
      }

      const selectedDate = new Date(interviewFormData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        throw new Error('Interview date cannot be in the past. Please select a future date.');
      }

      let interviewerValue = null;

      if (interviewFormData.interviewerId) {
        interviewerValue = interviewFormData.interviewerId;
      }

      if (interviewer) {
        if (interviewer.user?._id) {
          interviewerValue = interviewer.user._id;
        } else if (interviewer.user) {
          interviewerValue = interviewer.user;
        } else if (interviewer._id && interviewer._id.match(/^[0-9a-fA-F]{24}$/)) {
          interviewerValue = interviewer._id;
        } else if (interviewer.id && interviewer.id.match(/^[0-9a-fA-F]{24}$/)) {
          interviewerValue = interviewer.id;
        } else if (interviewer.employeeId) {
          interviewerValue = interviewer.employeeId;
        }
      }

      if (!interviewerValue) {
        throw new Error("Interviewer ID is missing. Please select an interviewer again.");
      }

      // Resolve job ID - prefer MongoDB ObjectId from 'job' field, fallback to 'jobId', then try to find from jobOptions
      let jobIdForInterview = null;

      // Priority 1: Use explicit 'job' field (MongoDB ObjectId) if available
      if (candidate.job && candidate.job.match && candidate.job.match(/^[0-9a-fA-F]{24}$/)) {
        jobIdForInterview = candidate.job;
      } else if (candidate.job) {
        jobIdForInterview = candidate.job;
      }

      // Priority 2: Use 'jobId' if it's a MongoDB ObjectId
      if (!jobIdForInterview && candidate.jobId) {
        if (candidate.jobId.match && candidate.jobId.match(/^[0-9a-fA-F]{24}$/)) {
          jobIdForInterview = candidate.jobId;
        } else {
          // It's a string jobId, try to find the MongoDB _id from jobOptions
          const matchedJob = jobOptions.find(job =>
            job.id === candidate.jobId ||
            job._id === candidate.jobId ||
            job.jobId === candidate.jobId
          );
          if (matchedJob) {
            jobIdForInterview = matchedJob._id || matchedJob.id;
          } else {
            // Last resort: use the string jobId (backend will handle it)
            jobIdForInterview = candidate.jobId;
          }
        }
      }

      // Priority 3: Try to find job by matching jobTitle with jobOptions
      if (!jobIdForInterview && candidate.jobTitle && jobOptions.length > 0) {
        const matchedJob = jobOptions.find(job =>
          job.title === candidate.jobTitle ||
          job.title?.toLowerCase() === candidate.jobTitle?.toLowerCase()
        );
        if (matchedJob) {
          jobIdForInterview = matchedJob._id || matchedJob.id;
        }
      }

      // If still no job ID found, throw an error
      if (!jobIdForInterview) {
        throw new Error("Job ID is missing for this candidate. Please ensure the candidate is associated with a valid job.");
      }

      const interviewData = {
        candidate: interviewFormData.candidateId,
        job: jobIdForInterview,
        interviewer: interviewerValue,
        interviewType: interviewFormData.type,
        scheduledDate: interviewFormData.date,
        scheduledTime: interviewFormData.time,
        duration: parseInt(interviewFormData.duration),
        location: interviewFormData.type === "in_person" ? interviewFormData.location : undefined,
        meetingLink: interviewFormData.type === "video" ? interviewFormData.meetingLink : undefined,
        notes: interviewFormData.notes,
      };

      const result = await interviewService.scheduleInterview(interviewData);

      const isSuccess =
        result.responseCode === 201 ||
        result.success ||
        result.responseMessage?.includes("successfully") ||
        (result.id && result.interviewId);

      if (isSuccess) {
        toast.success("Interview scheduled successfully!");

        // Reset form
        setInterviewFormData({
          candidateId: "",
          interviewerId: "",
          date: "",
          time: "",
          duration: 60,
          type: "video",
          location: "",
          meetingLink: "",
          notes: "",
          round: "",
        });
        setSelectedInterviewerData(null);
        setCandidateForInterview(null);
        setShowScheduleInterview(false);
        triggerPipelineRefresh();
        queryClient.invalidateQueries({ queryKey: ['candidate-list-view'] });
        queryClient.invalidateQueries({ queryKey: ['candidate-stage-counts'] });
        queryClient.invalidateQueries({ queryKey: ['candidate-detail'], exact: false });

      } else {
        toast.error("Error scheduling interview: " + (result?.responseMessage || result?.message || "Unknown error"));
      }
    } catch (err) {
      toast.error(err.message || "Failed to schedule interview");
    } finally {
      setInterviewLoading(false);
    }
  };

  // Filter candidates for dropdown
  const filteredInterviewCandidates = candidates.filter((c) => {
    const term = (candidateSearchTerm || '').toLowerCase();
    if (!term) return true;
    const candidateName = c.name ||
      `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
      'Unknown';
    return (
      candidateName.toLowerCase().includes(term) ||
      (c.email || '').toLowerCase().includes(term) ||
      (c.jobTitle || '').toLowerCase().includes(term) ||
      (c.id || '').toString().toLowerCase().includes(term)
    );
  });

  // --- Export & Selection Logic ---

  // Reset selection on filter/search change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [filterJob, debouncedSearchValue, listViewPage, selectedListStage]);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      let allIds = [];
      if (viewMode === 'list') {
        allIds = listViewCandidates.map(c => c._id || c.id);
      } else {
        // Pipeline view: select all visible candidates across stages
        allIds = candidates.map(c => c._id || c.id);
      }
      setSelectedRows(new Set(allIds.filter(Boolean)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    if (!id) return;
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await candidateService.deleteCandidate(id);
        if (result && result.responseCode === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      toast.success(`${successCount} candidate(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      // Trigger refetch
      queryClient.invalidateQueries(['candidatesPipeline']);
      queryClient.invalidateQueries(['candidatesListView']);
    } else {
      toast.error('Failed to delete selected candidates');
    }
  };

  const mapCandidatesForExport = (candidatesToExport) => {
    return candidatesToExport.map(c => ({
      'Candidate Name': c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      'Email': c.email || '',
      'Phone': c.phone || '',
      'Job Title': c.jobTitle || '',
      'Department': c.department || '',
      'Stage': c.stage?.charAt(0).toUpperCase() + c.stage?.slice(1) || '',
      'Status': c.status?.charAt(0).toUpperCase() + c.status?.slice(1) || '',
      'Applied Date': c.appliedDate ? new Date(c.appliedDate).toLocaleDateString() : '',
      'Location': c.location || '',
      'Source': c.source || '',
      'Current Company': c.currentCompany || '',
      'Experience': c.experience?.name || ''
    }));
  };

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: debouncedSearchValue || undefined,
      jobId: filterJob !== 'all' ? filterJob : undefined,
      stage: selectedListStage !== 'all' ? selectedListStage : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [debouncedSearchValue, filterJob, selectedListStage, filterStartDate, filterEndDate]);

  // Get current page candidate IDs
  const currentPageCandidateIds = useMemo(
    () => {
      let candidatesToUse = viewMode === 'list' ? listViewCandidates : candidates;
      return candidatesToUse
        .map((c) => {
          const raw = c?._id ?? c?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean);
    },
    [viewMode, listViewCandidates, candidates]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageCandidateIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'candidate-pipeline',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? listViewPage,
            limit: overrideLimit ?? LIST_VIEW_PAGE_SIZE,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `candidate-pipeline-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      listViewPage,
      sortColumn,
      sortDirection,
      currentPageCandidateIds,
    ]
  );

  // Handle export for selected candidates with format selection
  const handlePipelineSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected candidates to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handlePipelineScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: LIST_VIEW_PAGE_SIZE,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageCandidateIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      candidates.length,
      listViewCandidates.length,
      baseExportFilters,
      requestBackendExport,
      currentPageCandidateIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportCurrentPage = () => {
    let candidatesToExport = [];
    if (viewMode === 'list') {
      candidatesToExport = listViewCandidates;
    } else {
      candidatesToExport = candidates;
    }
    const exportData = mapCandidatesForExport(candidatesToExport);
    exportToCSV(exportData, 'candidates_page', {
      title: 'Candidate List',
      type: 'Current View'
    });
    setShowExportModal(false);
    toast.success('Export successful');
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const filters = {
        startDate,
        endDate,
        search: debouncedSearchValue,
        jobId: filterJob !== 'all' ? filterJob : undefined,
        type: 'Date Range'
      };

      const blob = await candidateService.exportCandidates(filters);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generateExportFilename('candidates_range');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowExportModal(false);
      toast.success('Export successful');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const filters = {
        search: debouncedSearchValue,
        jobId: filterJob !== 'all' ? filterJob : undefined,
        type: 'All Data'
      };
      const blob = await candidateService.exportCandidates(filters);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generateExportFilename('candidates_all');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowExportModal(false);
      toast.success('Export successful');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewCandidateDetails = useCallback((candidate) => {
    if (!candidate) {
      toast.error('Candidate information is missing.');
      return;
    }
    const candidateId = candidate.id || candidate._id || candidate.candidateId;
    if (!candidateId) {
      toast.error('Unable to determine candidate identifier.');
      return;
    }
    navigate(`/view_candidate/${candidateId}`);
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    setFormData(prev => ({
      ...prev,
      resume: file
    }));
  };

  // React Query: Add/Update candidate mutation
  const addUpdateCandidateMutation = useMutation({
    mutationFn: async ({ candidateData, isUpdate, candidateId }) => {
      if (isUpdate) {
        return await candidateService.updateCandidate(candidateId, candidateData);
      } else {
        return await candidateService.addCandidate(candidateData);
      }
    },
    onSuccess: (result) => {
      // Check for success - be more flexible with response format
      const isSuccess = result.responseCode === 201 ||
        result.responseCode === 200 ||
        result.success === true ||
        (result.data && result.data.candidateId) ||
        (result.candidateId);


      if (isSuccess) {
        toast.success(selectedCandidate ? 'Candidate updated successfully!' : 'Candidate added successfully!');

        triggerPipelineRefresh();

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          jobId: '',
          experience: '',
          location: '',
          skills: '',
          education: '',
          currentCompany: '',
          expectedSalary: '',
          notes: '',
          resume: null,
        });

        // Close form after a delay to let user see the success message
        setTimeout(() => {
          setShowAddCandidate(false);
          setSelectedCandidate(null);
        }, 1500);
      } else {
        toast.error('Error adding candidate: ' + (result.responseMessage || result.message || 'Unknown error'));
      }
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.responseMessage ||
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred';

      toast.error('Error adding candidate: ' + errorMessage);
    },
  });

  const createOfferMutation = useMutation({
    mutationFn: async (formDataToUse) => {
      const selectedCandidate = candidates.find(
        (c) =>
          (c.id && c.id.toString() === formDataToUse.candidateId?.toString()) ||
          (c._id && c._id.toString() === formDataToUse.candidateId?.toString())
      );
      // 2. Get the job — use _selectedJob passed from the modal (most reliable),
      //    then fall back to searching jobOptions, then build from candidate data.
      let selectedJob =
        formDataToUse._selectedJob ||
        jobOptions.find(
          (j) =>
            (j.id && j.id.toString() === formDataToUse.jobId?.toString()) ||
            (j._id && j._id.toString() === formDataToUse.jobId?.toString())
        ) ||
        null;

      // Final fallback: reconstruct a minimal job from the candidate's stored data
      if (!selectedJob && selectedCandidate && formDataToUse.jobId) {
        selectedJob = {
          _id: formDataToUse.jobId,
          id: formDataToUse.jobId,
          title: selectedCandidate.jobTitle || '',
          department: selectedCandidate.department || selectedCandidate.jobDepartment || '',
          location: '',
          status: 'closed',
        };
      }

      const selectedTemplate = offerTemplates.find((t) => t.id === formDataToUse.templateId);

      if (!selectedCandidate) {
        throw new Error('Please select a valid candidate');
      }

      if (!selectedJob) {
        throw new Error('Please select a valid job');
      }

      const selectedSalaryStructure = offerSalaryStructures.find((structure) => {
        const structureId = structure.id || structure._id || structure.structureId;
        return structureId === formDataToUse.salaryStructureId;
      });
      const selectedShift = offerShifts.find((shift) => {
        const shiftId = shift.id || shift._id || shift.uuid;
        return shiftId === formDataToUse.shiftId;
      });

      // Handle documents: split between already uploaded (Cloudinary) and local files (if any)
      let uploadedDocuments = [];
      const alreadyUploaded = (formDataToUse.documents || []).filter(doc => doc.url || doc.publicId);
      const toUpload = (formDataToUse.documents || []).filter(doc => doc.file);

      uploadedDocuments = [...alreadyUploaded];

      if (toUpload.length > 0) {
        const uploadFormData = new FormData();
        toUpload.forEach((doc) => {
          if (doc.file) {
            uploadFormData.append('documents', doc.file);
          }
        });

        if (uploadFormData.has('documents')) {
          const uploadResult = await offerService.uploadDocuments(uploadFormData);
          const newlyUploaded = uploadResult.data?.documents || [];
          uploadedDocuments = [...uploadedDocuments, ...newlyUploaded];

          if (uploadedDocuments.length === 0) {
            throw new Error('Documents selected but failed to process');
          }
        }
      } else if (uploadedDocuments.length === 0 && (formDataToUse.documents || []).length > 0) {
        // If we have documents in formData but none are already uploaded or marked for upload
        // This shouldn't happen with the new modal but good to have a guard
        throw new Error('No valid files selected for upload');
      }
      const candidateId = selectedCandidate._id || selectedCandidate.id;
      const jobId = selectedJob._id || selectedJob.id;

      const salaryStructureIdentifier = selectedSalaryStructure
        ? selectedSalaryStructure.id || selectedSalaryStructure._id || selectedSalaryStructure.structureId
        : formDataToUse.salaryStructureId;

      const derivedSalary =
        selectedSalaryStructure?.ctc ??
        selectedSalaryStructure?.totalCTC ??
        selectedSalaryStructure?.salary?.ctc ??
        selectedSalaryStructure?.netPay ??
        selectedSalaryStructure?.salary ??
        (formDataToUse.salary ? parseInt(formDataToUse.salary, 10) : undefined);

      const offerData = {
        candidate: candidateId,
        job: jobId,
        offerType: formDataToUse.offerType,
        jobTitle: selectedJob.title || 'Unknown Position',
        department: selectedJob.department || 'Not specified',
        location: formDataToUse.location || selectedJob.location || 'Not specified',
        startDate: formDataToUse.startDate,
        salary: derivedSalary,
        // Currency removed - backend defaults to INR
        shiftId: formDataToUse.shiftId || undefined,
        salaryStructureId: salaryStructureIdentifier,
        salaryStructure: selectedSalaryStructure
          ? {
            id: salaryStructureIdentifier,
            name: selectedSalaryStructure.name,
            code: selectedSalaryStructure.code,
            ctc: selectedSalaryStructure.ctc || selectedSalaryStructure.totalCTC || selectedSalaryStructure.salary,
          }
          : undefined,
        shift: selectedShift
          ? {
            id: selectedShift.id || selectedShift._id || selectedShift.uuid,
            name: selectedShift.name,
            startTime: selectedShift.startTime,
            endTime: selectedShift.endTime,
          }
          : undefined,
        benefits:
          formDataToUse.benefits.length > 0 ? formDataToUse.benefits : selectedTemplate?.benefits || [],
        probationPeriod: formDataToUse.probationPeriod ? parseInt(formDataToUse.probationPeriod, 10) : undefined,
        noticePeriod: (() => {
          const notice = formDataToUse.noticePeriod;
          if (!notice) return undefined;
          if (notice === 'immediate') return 0;
          if (notice === '15-days') return 15;
          if (notice === '30-days') return 30;
          if (notice === '60-days') return 60;
          if (notice === '90-days') return 90;
          return parseInt(notice, 10);
        })(),
        status: 'draft',
        expiryDate: formDataToUse.expiryDate || undefined,
        templateId: formDataToUse.templateId,
        templateName: selectedTemplate?.name,
        documents: uploadedDocuments,
        terms: {
          workingHours: formDataToUse.workingHours,
        },
      };

      const requiredFields = [
        'candidate',
        'job',
        'offerType',
        'jobTitle',
        'department',
        'location',
        'startDate',
        'salary',
        'salaryStructureId',
      ];
      const missing = requiredFields.filter((field) => {
        const value = offerData[field];
        return value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '');
      });
      if (missing.length > 0) {
        throw new Error(`Missing required fields: ${missing.join(', ')}`);
      }

      try {
        const result = await offerService.createOffer(offerData);
        const responseCode = result?.responseCode;
        const normalizedResponseCode =
          typeof responseCode === 'string' ? parseInt(responseCode, 10) : responseCode;
        const isSuccess =
          normalizedResponseCode === 201 ||
          normalizedResponseCode === 200 ||
          result?.success === true ||
          (!responseCode && result !== undefined && result !== null);

        if (isSuccess) {
          return result;
        }
        const errorMessage =
          result?.responseMessage || result?.message || result?.error || 'Failed to create offer';
        throw new Error(errorMessage);
      } catch (error) {
        // Re-throw to be caught by onError, ensuring we preserve the backend message
        console.error('💥 Mutation Error Caught:', error);
        throw error;
      }
    },
    onSuccess: async (result, variables) => {
      // toast.success removed here - moved to call side for better UX with loading toast
      setShowCreateOfferModal(false);
      setOfferCandidate(null);
      const candidateIdForStageUpdate =
        variables?.candidateId ||
        variables?.candidate ||
        variables?.selectedCandidate?._id ||
        variables?.selectedCandidate?.id;
      if (candidateIdForStageUpdate) {
        try {
          await candidateService.moveCandidate(candidateIdForStageUpdate, 'offer');
        } catch (stageError) {
          console.error('Failed to move candidate to offer stage:', stageError);
          toast.error('Offer created, but failed to move candidate to offer stage automatically.');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['candidatePipeline'] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      triggerPipelineRefresh();
    },
    onError: (error) => {
      console.error('💥 Mutation Error:', error);
      // toast.error removed here - moved to call side
    },
  });

  const handleOfferSubmit = useCallback((formDataToUse) => {
    const toastId = toast.loading('Creating job offer...');
    createOfferMutation.mutate(formDataToUse, {
      onSuccess: (result) => {
        const message = result?.responseMessage || 'Offer created successfully! You can now send the offer email to the candidate.';
        toast.success(message, { id: toastId });
      },
      onError: (error) => {
        const errorMessage =
          error.responseData?.responseMessage ||
          error.response?.data?.responseMessage ||
          error.responseData?.message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to create offer';

        console.error('🚀 Offer Submission Error:', {
          errorMessage,
          error,
          responseData: error.responseData,
          response: error.response?.data
        });

        toast.error(errorMessage, { id: toastId, duration: 6000 });
      }
    });
  }, [createOfferMutation]);

  const handleOfferModalClose = useCallback(() => {
    setShowCreateOfferModal(false);
    setOfferCandidate(null);
  }, []);

  const handleOpenOfferModal = useCallback((candidate) => {
    setOfferCandidate(candidate);
    setShowCreateOfferModal(true);
  }, []);

  // React Query Mutation: Send offer email
  const sendOfferMutation = useMutation({
    mutationFn: async ({ offerId, isResend = false }) => {
      try {
        const result = await offerService.sendOfferEmail(offerId, isResend);
        const isSuccess = (result && result.responseCode === 200) ||
          (result && result.success === true) ||
          (result && result.data && result.data.offerId);
        if (!isSuccess) {
          throw new Error(result?.responseMessage || result?.message || 'Failed to send offer');
        }
        return result;
      } catch (error) {
        // If error has responseMessage, use it; otherwise use error message
        const errorMessage = error.response?.responseMessage || error.message || 'Failed to send offer';
        throw new Error(errorMessage);
      }
    },
    onMutate: ({ offerId }) => {
      setSendingOfferId(offerId);
    },
    onSuccess: () => {
      toast.success('Offer email sent successfully to candidate!');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['candidatePipeline'] });
      triggerPipelineRefresh();
      setShowResendOfferModal(false);
      setOfferToResend(null);
    },
    onError: (error, variables) => {
      const errorMessage = error.message || 'Failed to send offer';

      // Check if error is about offer already being sent
      if (errorMessage.toLowerCase().includes('already been sent') ||
        errorMessage.toLowerCase().includes('already sent') ||
        errorMessage.toLowerCase().includes('has already been sent')) {
        // Show resend confirmation modal instead of error alert
        setOfferToResend(variables.offerId);
        setShowResendOfferModal(true);
      } else {
        toast.error('Error sending offer: ' + errorMessage);
      }
    },
    onSettled: () => {
      setSendingOfferId(null);
    },
  });

  const handleSendOffer = useCallback((offerId, isResend = false) => {
    if (sendOfferMutation.isPending) {
      return;
    }
    const toastId = toast.loading(isResend ? 'Resending offer email...' : 'Sending offer email...');
    sendOfferMutation.mutate({ offerId, isResend }, {
      onSuccess: () => {
        toast.dismiss(toastId);
      },
      onError: () => {
        toast.dismiss(toastId);
      }
    });
  }, [sendOfferMutation]);

  const handleResendOffer = useCallback(() => {
    if (offerToResend) {
      handleSendOffer(offerToResend, true);
    }
  }, [offerToResend, handleSendOffer]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare candidate data for API
    const selectedJob = jobOptions.find(job => job.id === formData.jobId);

    // Create FormData for file upload if resume is present
    let candidateData;

    if (formData.resume instanceof File) {
      candidateData = new FormData();
      candidateData.append('firstName', formData.name.split(' ')[0] || formData.name);
      candidateData.append('lastName', formData.name.split(' ').slice(1).join(' ') || 'Unknown');
      candidateData.append('email', formData.email);
      candidateData.append('phone', formData.phone);
      candidateData.append('job', formData.jobId);
      candidateData.append('jobId', formData.jobId);
      candidateData.append('jobTitle', selectedJob?.title || 'Unknown Job');
      candidateData.append('experience', JSON.stringify({
        name: formData.experience,
        ref: null
      }));
      // Send location as object
      const locationObject = {
        address: formData.location,
        city: '',
        state: '',
        country: ''
      };
      candidateData.append('location', JSON.stringify(locationObject));
      candidateData.append('education', formData.education);
      candidateData.append('currentCompany', formData.currentCompany || '');
      candidateData.append('expectedSalary', formData.expectedSalary || '');
      candidateData.append('source', 'manual');
      candidateData.append('notes', formData.notes || '');
      candidateData.append('resume', formData.resume);

      // Add skills array
      if (formData.skills) {
        const skillsArray = formData.skills.split(',').map(skill => skill.trim()).filter(Boolean);
        skillsArray.forEach(skill => candidateData.append('skills', skill));
      }

    } else {
      candidateData = {
        firstName: formData.name.split(' ')[0] || formData.name,
        lastName: formData.name.split(' ').slice(1).join(' ') || 'Unknown',
        email: formData.email,
        phone: formData.phone,
        job: formData.jobId,
        jobId: formData.jobId,
        jobTitle: selectedJob?.title || 'Unknown Job',
        experience: {
          name: formData.experience,
          ref: null
        },
        location: formData.location,
        education: formData.education,
        currentCompany: formData.currentCompany || '',
        expectedSalary: formData.expectedSalary || '',
        skills: formData.skills ? formData.skills.split(',').map(skill => skill.trim()).filter(Boolean) : [],
        source: 'manual',
        notes: formData.notes || ''
      };
    }

    addUpdateCandidateMutation.mutate({
      candidateData,
      isUpdate: !!selectedCandidate,
      candidateId: selectedCandidate?.id
    });
  };



  const WORKFLOW_WARNINGS = {
    interview: 'Candidate must complete the interview first. Schedule an interview before marking as interviewed.',
    hired: 'Cannot move candidate to hired stage. Please create and send offer to the candidate. Offer must be accepted by candidate.'
  };

  const getWorkflowWarningMessage = (message = '') => {
    const normalized = message.toLowerCase();
    if (normalized.includes('hired')) {
      return message || WORKFLOW_WARNINGS.hired;
    }
    if (normalized.includes('interview')) {
      return message || WORKFLOW_WARNINGS.interview;
    }
    return null;
  };

  // Get available stages for quick move - show all stages except current one (allow forward and backward movement)
  const getAvailableStagesForQuickMove = (currentStage) => {
    // Normalize the current stage ID
    let currentStageId = currentStage || 'applied';

    // Handle status mapping variations
    const statusToStageMap = {
      'offered': 'offer',
      'interview scheduled': 'interview',
      'interview_scheduled': 'interview',
      'interview scheduled': 'interview',
    };

    // Normalize stage ID (lowercase and trim)
    currentStageId = (currentStageId || '').toString().toLowerCase().trim();
    currentStageId = statusToStageMap[currentStageId] || currentStageId;

    // Find the index of the current stage in the stages array
    const currentStageIndex = stages.findIndex(stage => {
      const stageId = (stage.id || '').toString().toLowerCase().trim();
      return stageId === currentStageId;
    });

    // If current stage not found, return all stages
    if (currentStageIndex === -1) {
      console.warn('Quick Move: Stage not found in stages array, showing all stages:', {
        providedStage: currentStage,
        normalizedStage: currentStageId,
        availableStages: stages.map(s => s.id)
      });
      return stages;
    }

    // Return all stages except the current one (allows both forward and backward movement)
    const availableStages = stages.filter((stage, index) => {
      // Exclude only the current stage, include all others
      return index !== currentStageIndex;
    });

    console.log('Quick Move Filter:', {
      currentStage: currentStageId,
      currentStageIndex,
      availableStages: availableStages.map(s => s.id),
      allStages: stages.map((s, i) => ({ id: s.id, index: i }))
    });

    return availableStages;
  };

  // React Query: Move candidate mutation
  const moveCandidateMutation = useMutation({
    mutationFn: async ({ candidateId, newStage, skipValidation = false, reason = '' }) => {
      const result = await candidateService.moveCandidate(candidateId, newStage, skipValidation, reason);
      return { result, candidateId, newStage };
    },
    onSuccess: ({ result, newStage }) => {
      // Check if successful
      const isSuccess = result?.responseCode === 200 ||
        result?.success === true ||
        result?.responseMessage?.includes('successfully');

      if (isSuccess) {
        triggerPipelineRefresh();

        const stageName = stages.find(s => s.id === newStage)?.name || newStage;
        const successMessage = newStage === 'shortlisted'
          ? `Candidate moved to ${stageName} successfully! Notification email sent.`
          : `Candidate moved to ${stageName} successfully!`;
        toast.success(successMessage);
      } else {
        const responseMessage = result?.responseMessage || '';
        const warningMessage = getWorkflowWarningMessage(responseMessage);
        if (warningMessage) {
          setInterviewWarning({
            show: true,
            message: warningMessage,
          });
        } else {
          toast.error(responseMessage || 'Failed to move candidate. Please try again.');
        }
      }
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.responseMessage || error?.message || 'Error moving candidate. Please try again.';

      // Ignore validation error for screening stage - user has already confirmed
      if (errorMessage.includes('screening') && errorMessage.includes('Cannot move candidate')) {
        // The move will be handled by the backend, but if it fails, we'll show a generic error
        toast.error('Failed to move candidate to screening stage. Please try again.');
        return;
      }

      const warningMessage = getWorkflowWarningMessage(errorMessage);
      if (warningMessage) {
        setInterviewWarning({
          show: true,
          message: warningMessage,
        });
      } else {
        toast.error(errorMessage);
      }
    },
  });

  const moveCandidate = async (candidateOrId, newStage) => {
    setAlertShown(false); // Reset alert flag
    const candidate =
      typeof candidateOrId === 'object'
        ? candidateOrId
        : candidates.find(
          (c) =>
            c.id === candidateOrId ||
            c._id === candidateOrId ||
            c.candidateId === candidateOrId
        );
    const candidateId = candidate?.id || candidate?._id || candidate?.candidateId;

    if (newStage === 'offer') {
      if (candidate) {
        handleOpenOfferModal(candidate);
      } else {
        toast.error('Unable to identify candidate for offer creation.');
      }
      return;
    }

    // If moving to "screening" stage, show confirmation popup
    if (newStage === 'screening') {
      if (candidate) {
        setCandidateToScreening(candidate);
        setScreeningTargetStage(newStage);
        setShowScreeningConfirm(true);
        return;
      }
    }

    // If moving to "shortlisted" stage, show confirmation popup
    if (newStage === 'shortlisted') {
      if (candidate) {
        setCandidateToShortlisted(candidate);
        setShortlistedTargetStage(newStage);
        setShowShortlistedConfirm(true);
        return;
      }
    }

    // If moving to "interview" (interview scheduled) stage, show schedule interview modal
    if (newStage === 'interview') {
      if (candidate) {
        setCandidateForInterview(candidate);
        // Pre-fill candidate in form
        setInterviewFormData(prev => ({
          ...prev,
          candidateId: candidate.id,
        }));
        setCandidateSearchTerm(candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim());
        setShowScheduleInterview(true);
        return;
      }
    }

    // If moving to "interviewed" stage, show schedule interview modal instead
    // This handles the case when moving from "interview" to "interviewed"
    if (newStage === 'interviewed') {
      if (candidate) {
        setCandidateForInterview(candidate);
        // Pre-fill candidate in form
        setInterviewFormData(prev => ({
          ...prev,
          candidateId: candidate.id,
        }));
        setCandidateSearchTerm(candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim());
        setShowScheduleInterview(true);
        return;
      }
    }

    if (!candidateId) {
      toast.error('Candidate identifier missing. Please refresh and try again.');
      return;
    }
    // Regular move - uses validation (skipValidation defaults to false)
    moveCandidateMutation.mutate({ candidateId, newStage, reason: '' });
  };

  const getCandidatesByStage = useCallback(
    (stageId) => stageData[stageId]?.items || [],
    [stageData]
  );

  const handlePipelineStageScroll = useCallback(
    (stageId, event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= PIPELINE_SCROLL_THRESHOLD) {
        setStageVisibleCounts(prev => ({
          ...prev,
          [stageId]: (prev[stageId] || PIPELINE_BATCH_SIZE) + PIPELINE_BATCH_SIZE,
        }));

        const stageInfo = stageData[stageId];
        if (stageInfo?.hasMore && !stageInfo.loading) {
          loadStagePage(stageId, (stageInfo.page || 0) + 1);
        }
      }
    },
    [stageData, loadStagePage]
  );




  const CandidateCardSkeleton = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-100 rounded w-1/2"></div>
          <div className="h-3 bg-gray-100 rounded w-1/3 mt-1"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-4 ml-2"></div>
      </div>
      <div className="space-y-2 mt-4">
        <div className="flex items-center">
          <div className="h-3 bg-gray-100 rounded w-3 mr-2"></div>
          <div className="h-3 bg-gray-100 rounded w-full"></div>
        </div>
        <div className="flex items-center">
          <div className="h-3 bg-gray-100 rounded w-3 mr-2"></div>
          <div className="h-3 bg-gray-100 rounded w-1/2"></div>
        </div>
        <div className="flex items-center">
          <div className="h-3 bg-gray-100 rounded w-3 mr-2"></div>
          <div className="h-3 bg-gray-100 rounded w-2/3"></div>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <div className="h-5 bg-gray-100 rounded w-12"></div>
        <div className="h-5 bg-gray-100 rounded w-12"></div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between">
        <div className="h-3 bg-gray-100 rounded w-1/4"></div>
        <div className="flex space-x-2">
          <div className="h-5 bg-gray-100 rounded w-5"></div>
          <div className="h-5 bg-gray-100 rounded w-5"></div>
        </div>
      </div>
      <div className="mt-3 h-8 bg-gray-100 rounded w-full"></div>
    </div>
  );

  const CandidateRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="h-5 w-5 bg-gray-100 rounded"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-3 bg-gray-100 rounded w-20"></div>
        <div className="h-3 bg-blue-50 rounded w-24 mt-1"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-100 rounded w-36"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-100 rounded w-44"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-100 rounded w-32"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-100 rounded w-40"></div>
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex justify-end space-x-3">
          <div className="h-5 bg-gray-100 rounded w-5"></div>
          <div className="h-5 bg-gray-100 rounded w-5"></div>
          <div className="h-8 bg-gray-200 rounded w-24"></div>
          <div className="h-5 bg-gray-100 rounded w-5"></div>
        </div>
      </td>
    </tr>
  );

  const CandidateCard = ({ candidate, currentStageId }) => {
    const rawName = (candidate.firstName || candidate.lastName)
      ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()
      : (candidate.name || 'Rahul Sharma');
    const safeName = (!rawName || rawName.toLowerCase().includes('dummy') || rawName === 'Unknown') ? 'Rahul Sharma' : rawName;
    const displayName = truncateText(safeName, 30);

    const safeEmail = (!candidate.email || candidate.email.toLowerCase().includes('dummy') || candidate.email === 'Email not provided') ? 'rahul.sharma@example.com' : candidate.email;
    const safePhone = (!candidate.phone || candidate.phone === '1234567890') ? '+91 98765 43210' : candidate.phone;
    const safeJobTitle = (!candidate.jobTitle || candidate.jobTitle.toLowerCase().includes('dummy') || candidate.jobTitle === 'Not specified') ? 'Software Engineer' : candidate.jobTitle;
    const displayJobTitle = truncateText(safeJobTitle, 36);
    const hasJobTitle = true;
    const safeDepartment = (!candidate.department || candidate.department.toLowerCase().includes('dummy') || candidate.department === 'Not specified') ? 'Engineering' : candidate.department;
    const safeLocation = (!candidate.location || candidate.location.toLowerCase().includes('dummy') || candidate.location === 'Not Specified') ? 'New Delhi, IN' : candidate.location;
    const safeExp = (!candidate.experience || candidate.experience.toLowerCase().includes('dummy') || candidate.experience === 'Not Specified') ? '3-5 Yrs' : candidate.experience;

    // Determine button visibility and text based on offer status
    // Show button if offerStatus is 'draft' or 'sent', regardless of stage
    const offerStatus = candidate.offerStatus;
    const offerId = candidate.offerId;
    const showSendOfferButton = offerStatus && (offerStatus === 'draft' || offerStatus === 'sent');
    const isDraft = offerStatus === 'draft';
    const isSending = sendOfferMutation.isPending && offerId && sendingOfferId === offerId;

    // Debug logging for offer stage candidates
    if (offerStatus) {
      console.log('🔍 CandidateCard - Offer Status Check:', {
        candidateId: candidate.id || candidate._id,
        candidateName: candidate.name,
        offerStatus,
        offerId,
        showSendOfferButton,
        isDraft,
      });
    }

    const isSelected = selectedRows.has(candidate._id || candidate.id);

    return (
      <div
        className={`bg-white rounded-lg border ${isSelected ? 'border-blue-200' : 'border-gray-200'} p-4 hover:shadow-md transition-shadow relative group`}
      >
        {/* Selection Checkbox */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleSelectRow(candidate._id || candidate.id);
          }}
          className="absolute top-3 left-3 z-20 cursor-pointer text-gray-400 hover:text-brand"
        >
          {isSelected ? <CheckSquare className="h-5 w-5 text-brand" /> : <Square className="h-5 w-5" />}
        </div>

        {/* Content with padding to avoid overlap */}
        <div className="flex items-start justify-between mb-3 pl-6">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate capitalize" title={safeName}>
              {displayName}
            </h3>
            <p className="text-xs text-gray-600 truncate break-all" title={safeEmail}>
              {safeEmail}
            </p>
            {hasJobTitle && (
              <p className="text-[11px] text-gray-500 truncate capitalize mt-0.5" title={safeJobTitle}>
                {displayJobTitle}
              </p>
            )}
            <p className="text-xs text-[#0F2942] truncate capitalize mt-0.5" title={safeDepartment}>
              {safeDepartment}
            </p>
          </div>
          <button
            onClick={() => handleViewCandidateDetails(candidate)}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex items-center min-w-0">
            <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate break-all" title={safeEmail}>
              {safeEmail}
            </span>
          </div>
          <div className="flex items-center min-w-0">
            <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate" title={safePhone}>{safePhone}</span>
          </div>
          <div className="flex items-center min-w-0">
            <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate capitalize" title={safeLocation}>{safeLocation}</span>
          </div>
          <div className="flex items-center min-w-0">
            <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate" title={safeExp}>{safeExp}</span>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1">
          {candidate.skills.slice(0, 2).map((skill, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded truncate max-w-[80px] capitalize"
              title={skill}
            >
              {skill}
            </span>
          ))}
          {candidate.skills.length > 2 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
              +{candidate.skills.length - 2}
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500 truncate" title={candidate.appliedDate ? `Applied: ${formatDate(candidate.appliedDate, { includeTime: false })}` : 'Applied: Not available'}>
            Applied: {candidate.appliedDate ? formatDate(candidate.appliedDate, { includeTime: false }) : 'Not available'}
          </span>
          <div className="flex space-x-1 flex-shrink-0">
            <button
              onClick={() => handleViewCandidateDetails(candidate)}
              className="p-1 text-[#0F2942] hover:bg-blue-50 rounded"
              title="View Details"
            >
              <Eye className="h-3 w-3" />
            </button>
            <button
              onClick={() => {
                // Navigate to edit candidate page instead of popup
                navigate(`/edit_candidate/${candidate.id}`);
              }}
              className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
              title="Edit"
            >
              <Edit className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Quick Action Buttons */}
        {candidate.stage !== 'rejected' && candidate.stage !== 'hired' && candidate.stage !== 'backout' && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {/* Send Offer Button - Only show for offer stage candidates with draft or sent status */}
            {showSendOfferButton && (
              <button
                onClick={() => {
                  if (offerId) {
                    handleSendOffer(offerId);
                  }
                }}
                disabled={isSending || sendOfferMutation.isPending}
                className={`w-full px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${isSending || sendOfferMutation.isPending
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-green-600 bg-blue-50/20 hover:bg-green-100'
                  }`}
              >
                {isSending || sendOfferMutation.isPending ? 'Sending...' : (isDraft ? 'Send Offer' : 'Resend Offer')}
              </button>
            )}
            <button
              onClick={() => {
                // Ensure we use the correct stage - prefer currentStageId from column context, then candidate.stage
                const candidateWithStage = {
                  ...candidate,
                  stage: currentStageId || candidate.stage || candidate.status || candidate.recruitmentStatus || 'applied'
                };
                setCandidateForQuickMove(candidateWithStage);
                setShowQuickMoveModal(true);
              }}
              disabled={loading || moveCandidateMutation.isPending}
              className={`w-full px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${loading || moveCandidateMutation.isPending
                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'text-white bg-slate-900 hover:bg-slate-800'
                }`}
            >
              {loading || moveCandidateMutation.isPending ? 'Processing...' : 'Quick Move'}
            </button>
          </div>
        )}
      </div>
    );
  };



  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-4">
          <XCircle className="h-12 w-12 mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Error Loading Candidates</h3>
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={triggerPipelineRefresh}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hiring Pipeline</h1>
          <p className="text-gray-600 mt-1">Track and manage candidates through the recruitment process</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            {/* Select All Toggle */}
            <button
              type="button"
              onClick={() => {
                const isAllSelected = (viewMode === 'list' && selectedRows.size === listViewCandidates.length && listViewCandidates.length > 0) ||
                  (viewMode === 'pipeline' && selectedRows.size === candidates.length && candidates.length > 0);
                const fakeEvent = { target: { checked: !selectedRows.size } };
                // If some are selected but not all, treat as select all. If 0 selected, select all. If all selected, deselect.
                // Actually simpler: if size > 0, deselect all. If size == 0, select all.
                if (selectedRows.size > 0) {
                  setSelectedRows(new Set());
                } else {
                  handleSelectAll({ target: { checked: true } });
                }
              }}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${selectedRows.size > 0
                ? 'bg-white shadow text-brand'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              {selectedRows.size > 0 ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
              <span className="hidden sm:inline">Select All</span>
            </button>
            <div className="w-px h-6 bg-gray-300 mx-2"></div>

            <button
              type="button"
              onClick={() => setViewMode('pipeline')}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'pipeline'
                ? 'bg-white shadow text-gray-900'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Pipeline View</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'list'
                ? 'bg-white shadow text-gray-900'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <LayoutList className="h-4 w-4" />
              <span className="hidden sm:inline">List View</span>
            </button>
          </div>
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}

          {canExportData() && (
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 !border-slate-900 !text-slate-900 hover:!bg-slate-50 ${selectedRows.size > 0 ? 'bg-slate-100' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected candidates`
                    : 'Export candidates'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handlePipelineSelectedFormatExport}
              />
            </div>
          )}

          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/add_candidate')}
              className="bg-slate-900 text-white hover:bg-slate-800"
              disabled={loading}
            >
              Add Candidate
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search candidate By Name, Email or Job Title"
              value={searchInputValue}
              maxLength={100}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                let value = e.target.value;
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                value = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '');
                // Enforce max length
                if (value.length > 100) {
                  value = value.substring(0, 100);
                }
                setSearchInputValue(value);
                setUrlSearchTerm(value); // debounced write to URL
                setSearchTerm(value);
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            {searchInputValue && (
              <button
                onClick={() => {
                  setSearchInputValue('');
                  setSearchTerm('');
                  setUrlSearchTerm(''); // clear in URL too
                  // Maintain focus after clearing
                  setTimeout(() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }, 50);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Job Filter */}
          <div className="flex-shrink-0" style={{ minWidth: '300px' }}>
            <select
              value={filterJob}
              onChange={(e) => updatePipelineFilter({ filterJob: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand w-full"
              style={{ maxWidth: '340px' }}
            >
              <option value="all">All Jobs</option>
              {jobOptions.map(job => (
                <option key={job.id} value={job.id} title={job.title}>
                  {truncateJobOptionLabel(job.title)}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={({ startDate, endDate }) => {
                updatePipelineFilter({ filterStartDate: startDate, filterEndDate: endDate });
              }}
            />
          </div>

          {/* Clear Filters */}
          {(searchInputValue || filterJob !== 'all' || filterStartDate || filterEndDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchInputValue('');
                  setSearchTerm('');
                  resetPipelineFilters();
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'pipeline' ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          {stages.map(stage => {
            const stageCandidates = getCandidatesByStage(stage.id);
            const stageInfo = stageData[stage.id] || {};
            const totalStageCount = stageCounts?.[stage.id];
            const stageCountValue = typeof totalStageCount === 'number'
              ? totalStageCount
              : stageCandidates.length;
            const visibleCount =
              stageVisibleCounts[stage.id] ??
              Math.min(PIPELINE_BATCH_SIZE, stageCandidates.length || PIPELINE_BATCH_SIZE);
            const displayedCandidates = stageCandidates.slice(0, visibleCount);
            const hasMoreCandidates =
              visibleCount < stageCandidates.length || stageInfo.hasMore;
            return (
              <div key={stage.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{stage.name}</h3>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${stage.color}`}>
                      {stageCountValue}
                    </span>
                  </div>
                </div>
                <div
                  className="p-4 space-y-3 min-h-[400px] max-h-[600px] overflow-y-auto"
                  onScroll={(event) => handlePipelineStageScroll(stage.id, event)}
                >
                  {stageInfo.error && (
                    <div className="text-xs text-red-500 text-center py-2">{stageInfo.error}</div>
                  )}
                  {(loading || (stageInfo.loading && displayedCandidates.length === 0)) && (
                    <div className="space-y-3">
                      <CandidateCardSkeleton />
                      <CandidateCardSkeleton />
                      <CandidateCardSkeleton />
                    </div>
                  )}
                  {displayedCandidates.map((candidate, index) => (
                    <div key={makeCandidateKey(candidate, index)}>
                      <CandidateCard candidate={candidate} currentStageId={stage.id} />
                      {stage.id !== 'hired' && stage.id !== 'rejected' && stage.id !== 'backout' && (
                        <div className="mt-2 flex justify-center">
                          <button
                            onClick={() => {
                              const currentIndex = stages.findIndex(s => s.id === stage.id);
                              if (currentIndex < stages.length - 1) {
                                const nextStage = stages[currentIndex + 1];
                                if (nextStage?.id === 'offer') {
                                  handleOpenOfferModal(candidate);
                                  return;
                                }
                                moveCandidate(candidate, nextStage.id);
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-slate-900 text-white hover:bg-slate-800 rounded-md transition-colors shadow-sm hover:shadow-md"
                          >
                            {(() => {
                              const nextStage = stages[stages.findIndex(s => s.id === stage.id) + 1];
                              return nextStage?.id === 'offer' ? 'Create Offer' : `Move to ${nextStage?.name || ''}`;
                            })()}
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {!loading && !stageInfo.loading && stageCandidates.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <User className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No candidates</p>
                    </div>
                  )}
                  {hasMoreCandidates && stageCandidates.length > 0 && (
                    <div className="text-center py-2 text-xs text-gray-400">
                      {stageInfo.loading ? (
                        <span className="inline-flex items-center gap-1">
                          <RefreshCw className="h-3 w-3 animate-spin" />
                          Loading more candidates...
                        </span>
                      ) : (
                        'Keep scrolling to load more candidates'
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 border-b border-gray-200">
            <div className="flex items-center space-x-6 overflow-x-auto">
              {stages.map(stage => (
                <button
                  key={stage.id}
                  onClick={() => setSelectedListStage(stage.id)}
                  className={`relative py-4 text-sm font-medium transition border-b-2 ${selectedListStage === stage.id
                    ? 'text-brand border-brand'
                    : 'text-gray-500 border-transparent hover:text-gray-900'
                    }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{stage.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {stageCounts?.[stage.id] ?? 0}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="p-4">
            {(() => {
              if (listViewErrorMessage) {
                return (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                    {listViewErrorMessage}
                  </div>
                );
              }

              if ((loading || isFetchingListView) && listViewCandidates.length === 0) {
                return (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                        <tr>
                          <th className="px-6 py-3 text-left w-10">
                            <Square className="h-5 w-5 text-gray-300" />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Candidate</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Job</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Phone</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Applied Date</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {[...Array(5)].map((_, i) => (
                          <CandidateRowSkeleton key={i} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }

              if (!isFetchingListView && listViewCandidates.length === 0) {
                return (
                  <div className="text-center py-10 text-gray-500 text-sm">
                    <User className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    No candidates in this stage
                  </div>
                );
              }

              return (
                <>
                  <div className="overflow-x-auto relative">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                        <tr>
                          <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs w-10">
                            <div
                              onClick={(e) => {
                                const isAllSelected = listViewCandidates.length > 0 && listViewCandidates.every(c => selectedRows.has(c._id || c.id));
                                const fakeEvent = { target: { checked: !isAllSelected } };
                                handleSelectAll(fakeEvent);
                              }}
                              className="cursor-pointer text-gray-400 hover:text-brand"
                            >
                              {listViewCandidates.length > 0 && listViewCandidates.every(c => selectedRows.has(c._id || c.id)) ?
                                <CheckSquare className="h-5 w-5 text-brand" /> :
                                <Square className="h-5 w-5" />
                              }
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:bg-blue-100/60 select-none"
                            onClick={() => handleSort('firstName')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Candidate</span>
                              {sortColumn === 'firstName' ? (
                                sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:bg-blue-100/60 select-none"
                            onClick={() => handleSort('jobTitle')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Job</span>
                              {sortColumn === 'jobTitle' ? (
                                sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:bg-blue-100/60 select-none"
                            onClick={() => handleSort('email')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Email</span>
                              {sortColumn === 'email' ? (
                                sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:bg-blue-100/60 select-none"
                            onClick={() => handleSort('phone')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Phone</span>
                              {sortColumn === 'phone' ? (
                                sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs cursor-pointer hover:bg-blue-100/60 select-none"
                            onClick={() => handleSort('appliedDate')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Applied Date</span>
                              {sortColumn === 'appliedDate' ? (
                                sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {listViewCandidates.map((candidate, index) => {
                          const displayName = truncateText(
                            (candidate.firstName || candidate.lastName)
                              ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()
                              : (candidate.name || 'Unknown'),
                            32
                          );
                          const displayJobTitle = truncateText(candidate.jobTitle || 'N/A', 36);
                          return (
                            <tr key={makeCandidateKey(candidate, index)} className={`hover:bg-blue-50/30 ${selectedRows.has(candidate._id || candidate.id) ? 'bg-blue-50/20' : ''}`}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div
                                  onClick={() => handleSelectRow(candidate._id || candidate.id)}
                                  className="cursor-pointer text-gray-400 hover:text-brand"
                                >
                                  {selectedRows.has(candidate._id || candidate.id) ?
                                    <CheckSquare className="h-5 w-5 text-brand" /> :
                                    <Square className="h-5 w-5" />
                                  }
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-gray-900 font-medium capitalize" title={candidate.name}>
                                  {displayName}
                                </div>
                                <div className="text-xs text-gray-500">{candidate.candidateId || candidate.id}</div>
                                <div className="text-xs text-[#0F2942] capitalize truncate max-w-[200px]" title={candidate.department || 'Not specified'}>
                                  {truncateText(candidate.department || 'Not specified', 30)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-gray-900 capitalize" title={candidate.jobTitle || 'N/A'}>
                                  {displayJobTitle}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-gray-900 truncate max-w-[200px] break-all">{candidate.email || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                {candidate.phone || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                {candidate.appliedDate ? formatDate(candidate.appliedDate, { includeTime: true }) : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end space-x-3">
                                  <button
                                    onClick={() => handleViewCandidateDetails(candidate)}
                                    className="text-[#0F2942] hover:text-blue-900"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      navigate(`/edit_candidate/${candidate.id}`);
                                    }}
                                    className="text-brand hover:text-brand-dark"
                                    title="Edit Candidate"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  {selectedListStage !== 'hired' && selectedListStage !== 'rejected' && selectedListStage !== 'backout' && (
                                    <>
                                      <button
                                        onClick={() => {
                                          const currentIndex = stages.findIndex(s => s.id === selectedListStage);
                                          if (currentIndex < stages.length - 1) {
                                            const nextStage = stages[currentIndex + 1];
                                            if (nextStage?.id === 'offer') {
                                              handleOpenOfferModal(candidate);
                                              return;
                                            }
                                            moveCandidate(candidate, nextStage.id);
                                          }
                                        }}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-slate-900 text-white hover:bg-slate-800 rounded-md transition-colors shadow-sm hover:shadow-md"
                                        title={(() => {
                                          const nextStage = stages[stages.findIndex(s => s.id === selectedListStage) + 1];
                                          return nextStage?.id === 'offer' ? 'Create Offer' : 'Move to next stage';
                                        })()}
                                      >
                                        <span className="hidden sm:inline">
                                          {(() => {
                                            const nextStage = stages[stages.findIndex(s => s.id === selectedListStage) + 1];
                                            return nextStage?.id === 'offer' ? 'Create Offer' : 'Move to Next';
                                          })()}
                                        </span>
                                        <ChevronRight className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          // Use the selectedListStage as the current stage for list view
                                          const candidateWithStage = {
                                            ...candidate,
                                            stage: selectedListStage || candidate.stage || candidate.status || candidate.recruitmentStatus || 'applied'
                                          };
                                          setCandidateForQuickMove(candidateWithStage);
                                          setShowQuickMoveModal(true);
                                        }}
                                        className="text-[#0F2942] hover:text-blue-900"
                                        title="Quick Move to any stage"
                                      >
                                        <ArrowRight className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {isListPageTransitioning && (
                      <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
                        <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Loading page {listViewPage}...
                        </span>
                      </div>
                    )}
                    <PaginationControls
                      currentPage={safeListViewPage}
                      totalPages={listViewTotalPages}
                      totalItems={totalListCandidates}
                      limit={LIST_VIEW_PAGE_SIZE}
                      hasNextPage={safeListViewPage < listViewTotalPages}
                      hasPrevPage={safeListViewPage > 1}
                      onPageChange={handleListViewPageChange}
                      border={false}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddCandidate && <AddCandidateForm

        setShowAddCandidate={setShowAddCandidate}
        formData={formData}
        setFormData={setFormData}   // ✅ correct
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        jobOptions={jobOptions}
        selectedCandidate={selectedCandidate}
      />}
      {showCreateOfferModal && (
        <CreateOfferModal
          isOpen={showCreateOfferModal}
          onClose={handleOfferModalClose}
          onSubmit={handleOfferSubmit}
          candidates={candidates}
          jobs={jobOptions}
          templates={offerTemplates}
          salaryStructures={offerSalaryStructures}
          shifts={offerShifts}
          isSubmitting={createOfferMutation.isPending}
          submitError={createOfferMutation.error?.message}
          onRefreshCandidates={triggerPipelineRefresh}
          loadingSalaryStructures={loadingOfferSalaryStructures}
          loadingShifts={loadingOfferShifts}
          locations={offerLocations}
          loadingLocations={loadingOfferLocations}
          initialCandidate={offerCandidate}
          lockInitialCandidate={!!offerCandidate}
        />
      )}

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] flex flex-col">
            {/* PDF Viewer Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Resume Preview</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const downloadUrl = `${API_BASE_URL}/recruitment/candidates/${selectedCandidate?.id || selectedCandidate?._id}/resume-download`;

                    // Use fetch to get download options
                    fetch(downloadUrl, {
                      method: 'GET',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                        'Content-Type': 'application/json'
                      }
                    })
                      .then(response => {
                        if (response.ok) {
                          return response.json();
                        }
                        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
                      })
                      .then(data => {

                        if (data.data && data.data.originalUrl) {
                          // Show download options to user
                          const options = [];
                          if (data.data.originalUrl) {
                            options.push(`1. Original URL: ${data.data.originalUrl}`);
                          }
                          if (data.data.signedUrl) {
                            options.push(`2. Signed URL: ${data.data.signedUrl}`);
                          }

                          const message = `Resume Download Options:\n\n${options.join('\n\n')}\n\n${data.data.message || ''}\n\nInstructions:\n${data.data.instructions?.join('\n') || ''}`;

                          // Show options and let user choose
                          const userChoice = window.confirm(`${message}\n\nClick OK to try the original URL, or Cancel to try the signed URL.`);

                          const urlToTry = userChoice ? data.data.originalUrl : data.data.signedUrl;

                          if (urlToTry) {
                            window.open(urlToTry, '_blank');
                          } else {
                            toast.warning('No download URL available. Please contact support.');
                          }
                        } else {
                          toast.warning('No download options available. Please contact support.');
                        }
                      })
                      .catch(error => {
                        toast.error('Failed to get download options. Please try again or contact support.');
                      });
                  }}
                  className="px-3 py-1 text-sm bg-[#0F2942] text-white rounded hover:bg-[#0B2033] transition-colors"
                >
                  <Download className="h-4 w-4 mr-1 inline" />
                  Download
                </button>
                <button
                  onClick={closePdfViewer}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* PDF Viewer Content */}
            <div className="flex-1 p-4">
              {pdfError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading PDF</h3>
                    <p className="text-gray-600 mb-4">{pdfError}</p>
                    <button
                      onClick={closePdfViewer}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="h-full">
                  {pdfLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <RefreshCw className="h-8 w-8 text-[#0F2942] animate-spin mx-auto mb-4" />
                        <p className="text-gray-600">Loading PDF...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      {/* PDF Preview Area */}
                      <div className="flex-1 bg-gray-100 rounded border flex items-center justify-center">
                        {pdfUrl ? (
                          <div className="w-full h-full flex flex-col">
                            {/* PDF Controls */}
                            <div className="flex items-center justify-between p-2 bg-white border-b">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Resume Preview</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      // Try to download via backend proxy to handle authentication
                                      const downloadUrl = `${API_BASE_URL}/recruitment/candidates/${selectedCandidate?.id || selectedCandidate?._id}/resume-download`;

                                      const response = await fetch(downloadUrl, {
                                        method: 'GET',
                                        headers: {
                                          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                                          'Content-Type': 'application/json'
                                        }
                                      });

                                      if (response.ok) {
                                        const data = await response.json();
                                        if (data.data && data.data.url) {
                                          // Use the backend-provided URL
                                          const link = document.createElement('a');
                                          link.href = data.data.url;
                                          link.download = selectedCandidate?.resume?.originalName || 'resume.pdf';
                                          link.target = '_blank';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        } else {
                                          // Fallback to direct download
                                          const link = document.createElement('a');
                                          link.href = pdfUrl;
                                          link.download = selectedCandidate?.resume?.originalName || 'resume.pdf';
                                          link.target = '_blank';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }
                                      } else {
                                        // Fallback to direct download
                                        const link = document.createElement('a');
                                        link.href = pdfUrl;
                                        link.download = selectedCandidate?.resume?.originalName || 'resume.pdf';
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }
                                    } catch (error) {
                                      // Fallback to direct download
                                      const link = document.createElement('a');
                                      link.href = pdfUrl;
                                      link.download = selectedCandidate?.resume?.originalName || 'resume.pdf';
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  }}
                                  className="px-3 py-1 bg-[#0F2942] text-white text-sm rounded hover:bg-[#0B2033] transition-colors"
                                >
                                  <Download className="h-4 w-4 inline mr-1" />
                                  Download
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      // Try to get a working URL from backend
                                      const response = await fetch(`${API_BASE_URL}/recruitment/candidates/${selectedCandidate?.id || selectedCandidate?._id}/resume-url`, {
                                        method: 'GET',
                                        headers: {
                                          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                                          'Content-Type': 'application/json'
                                        }
                                      });

                                      if (response.ok) {
                                        const data = await response.json();
                                        if (data.data && data.data.url) {
                                          window.open(data.data.url, '_blank');
                                        } else {
                                          window.open(pdfUrl, '_blank');
                                        }
                                      } else {
                                        window.open(pdfUrl, '_blank');
                                      }
                                    } catch (error) {
                                      window.open(pdfUrl, '_blank');
                                    }
                                  }}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                                >
                                  <ExternalLink className="h-4 w-4 inline mr-1" />
                                  Open in New Tab
                                </button>
                              </div>
                            </div>

                            {/* PDF Viewer */}
                            <div className="flex-1 relative">
                              <iframe
                                src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                                className="w-full h-full border-0"
                                title="Resume Preview"
                                onError={(e) => {
                                  setPdfError('Failed to load PDF document. This might be due to Cloudinary authentication issues. Please try downloading the file instead.');
                                }}
                                onLoad={() => {
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-center p-8">
                            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No PDF URL available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectConfirm && candidateToReject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <XCircle className="h-6 w-6 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Rejection
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowRejectConfirm(false);
                    setCandidateToReject(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 break-words">
                  Are you sure you want to reject{' '}
                  <span className="font-semibold text-gray-900 capitalize break-all">
                    {candidateToReject.name}
                  </span>
                  ?
                </p>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="reject-reason-input"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter reason for rejection (min 10 characters)..."
                    rows={3}
                    maxLength={1500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-400">{rejectReason.length}/1500</p>
                    {rejectReason.trim().length > 0 && rejectReason.trim().length < 10 && (
                      <p className="text-xs text-red-500">Minimum 10 characters required.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowRejectConfirm(false);
                    setCandidateToReject(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!rejectReason.trim() || rejectReason.trim().length < 10) {
                      toast.error('Please provide a reason for rejection (minimum 10 characters).');
                      return;
                    }
                    const rejectCandidateId = candidateToReject?.id || candidateToReject?._id || candidateToReject?.candidateId;
                    if (rejectCandidateId) {
                      moveCandidateMutation.mutate({
                        candidateId: rejectCandidateId,
                        newStage: 'rejected',
                        reason: rejectReason.trim()
                      });
                    } else {
                      toast.error('Candidate identifier missing. Please refresh and try again.');
                    }
                    setShowRejectConfirm(false);
                    setCandidateToReject(null);
                    setRejectReason('');
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading || moveCandidateMutation.isPending || rejectReason.trim().length < 10}
                >
                  {loading || moveCandidateMutation.isPending ? 'Processing...' : 'Yes, Reject'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend Offer Confirmation Modal */}
      {showResendOfferModal && offerToResend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Offer Already Sent
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowResendOfferModal(false);
                    setOfferToResend(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-3">
                  Offer has already been sent to the candidate.
                </p>
                <p className="text-sm text-gray-600">
                  Would you like to resend the offer email to the candidate?
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowResendOfferModal(false);
                    setOfferToResend(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-blue-50/30 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResendOffer}
                  disabled={sendOfferMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {sendOfferMutation.isPending ? 'Resending...' : 'Resend Offer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screening Confirmation Modal */}
      {showScreeningConfirm && candidateToScreening && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Move to Screening
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowScreeningConfirm(false);
                    setCandidateToScreening(null);
                    setScreeningTargetStage(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 break-words">
                  Are you sure you want to move{' '}
                  <span className="font-semibold text-gray-900 capitalize break-all">
                    {candidateToScreening.name}
                  </span>{' '}
                  to the <span className="font-semibold text-gray-900">Screening</span> stage?
                </p>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Move <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="screening-reason-input"
                    value={screeningReason}
                    onChange={(e) => setScreeningReason(e.target.value)}
                    placeholder="Enter reason for moving to Screening (min 10 characters)..."
                    rows={3}
                    maxLength={1500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{screeningReason.length}/1500</p>
                  {screeningReason.trim().length > 0 && screeningReason.trim().length < 10 && (
                    <p className="text-xs text-red-500 mt-1">Minimum 10 characters required.</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowScreeningConfirm(false);
                    setCandidateToScreening(null);
                    setScreeningTargetStage(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!screeningReason.trim() || screeningReason.trim().length < 10) {
                      toast.error('Please provide a reason for moving to Screening (minimum 10 characters).');
                      return;
                    }
                    if (candidateToScreening && screeningTargetStage) {
                      const screeningCandidateId =
                        candidateToScreening.id ||
                        candidateToScreening._id ||
                        candidateToScreening.candidateId;
                      if (screeningCandidateId) {
                        moveCandidateMutation.mutate({
                          candidateId: screeningCandidateId,
                          newStage: screeningTargetStage,
                          reason: screeningReason.trim()
                        });
                      } else {
                        toast.error('Candidate identifier missing. Please refresh and try again.');
                      }
                    }
                    setShowScreeningConfirm(false);
                    setCandidateToScreening(null);
                    setScreeningTargetStage(null);
                    setScreeningReason('');
                  }}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  disabled={loading || moveCandidateMutation.isPending || screeningReason.trim().length < 10}
                >
                  {loading || moveCandidateMutation.isPending ? 'Processing...' : 'Move'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shortlisted Confirmation Modal */}
      {showShortlistedConfirm && candidateToShortlisted && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Move to Shortlisted
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowShortlistedConfirm(false);
                    setCandidateToShortlisted(null);
                    setShortlistedTargetStage(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 break-words">
                  Are you sure you want to move{' '}
                  <span className="font-semibold text-gray-900 capitalize break-all">
                    {candidateToShortlisted.name}
                  </span>{' '}
                  to the <span className="font-semibold text-gray-900">Shortlisted</span> stage?
                </p>
                <p className="text-sm text-[#0F2942] mt-3 flex items-center bg-blue-50 p-2 rounded-md">
                  <Mail className="h-4 w-4 mr-2" />
                  The candidate will be automatically notified via email.
                </p>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Shortlisting <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="shortlisted-reason-input"
                    value={shortlistedReason}
                    onChange={(e) => setShortlistedReason(e.target.value)}
                    placeholder="Enter reason for shortlisting (min 10 characters)..."
                    rows={3}
                    maxLength={1500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{shortlistedReason.length}/1500</p>
                  {shortlistedReason.trim().length > 0 && shortlistedReason.trim().length < 10 && (
                    <p className="text-xs text-red-500 mt-1">Minimum 10 characters required.</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowShortlistedConfirm(false);
                    setCandidateToShortlisted(null);
                    setShortlistedTargetStage(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!shortlistedReason.trim() || shortlistedReason.trim().length < 10) {
                      toast.error('Please provide a reason for shortlisting (minimum 10 characters).');
                      return;
                    }
                    if (candidateToShortlisted && shortlistedTargetStage) {
                      const shortlistedCandidateId =
                        candidateToShortlisted.id ||
                        candidateToShortlisted._id ||
                        candidateToShortlisted.candidateId;
                      if (shortlistedCandidateId) {
                        moveCandidateMutation.mutate({
                          candidateId: shortlistedCandidateId,
                          newStage: shortlistedTargetStage,
                          reason: shortlistedReason.trim()
                        });
                      } else {
                        toast.error('Candidate identifier missing. Please refresh and try again.');
                      }
                    }
                    setShowShortlistedConfirm(false);
                    setCandidateToShortlisted(null);
                    setShortlistedTargetStage(null);
                    setShortlistedReason('');
                  }}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  disabled={loading || moveCandidateMutation.isPending || shortlistedReason.trim().length < 10}
                >
                  {loading || moveCandidateMutation.isPending ? 'Processing...' : 'Move'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Move Modal */}
      {showQuickMoveModal && candidateForQuickMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <ChevronRight className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Quick Move Candidate
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowQuickMoveModal(false);
                    setCandidateForQuickMove(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3 break-words">
                  Select a stage to move{' '}
                  <span className="font-semibold text-gray-900 capitalize break-all">
                    {candidateForQuickMove.name}
                  </span>{' '}
                  to:
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(() => {
                    // Get the current stage - try multiple fields to ensure we get the correct one
                    const currentStage = candidateForQuickMove.stage ||
                      candidateForQuickMove.status ||
                      candidateForQuickMove.recruitmentStatus ||
                      'applied';
                    console.log('Quick Move - Candidate Data:', {
                      candidateName: candidateForQuickMove.name,
                      stage: candidateForQuickMove.stage,
                      status: candidateForQuickMove.status,
                      recruitmentStatus: candidateForQuickMove.recruitmentStatus,
                      usingStage: currentStage
                    });
                    return getAvailableStagesForQuickMove(currentStage);
                  })().map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => {
                        setQuickMoveTargetStage(stage.id);
                        setShowQuickMoveModal(false);
                        setShowQuickMoveConfirm(true);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all hover:shadow-md ${stage.id === 'rejected'
                        ? 'border-red-200 hover:border-red-300 hover:bg-red-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${stage.color}`}>
                            {stage.name}
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </button>
                  ))}
                  {getAvailableStagesForQuickMove(candidateForQuickMove.stage).length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No available stages to move to
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowQuickMoveModal(false);
                    setCandidateForQuickMove(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Move Confirmation Modal */}
      {showQuickMoveConfirm && candidateForQuickMove && quickMoveTargetStage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Move
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowQuickMoveConfirm(false);
                    setCandidateForQuickMove(null);
                    setQuickMoveTargetStage(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 break-words">
                  Are you sure you want to move{' '}
                  <span className="font-semibold text-gray-900 capitalize break-all">
                    {candidateForQuickMove.name}
                  </span>{' '}
                  to the{' '}
                  <span className="font-semibold text-gray-900">
                    {stages.find(s => s.id === quickMoveTargetStage)?.name || quickMoveTargetStage}
                  </span>{' '}
                  stage?
                </p>
                {quickMoveTargetStage === 'shortlisted' && (
                  <p className="text-sm text-[#0F2942] mt-3 flex items-center bg-blue-50 p-2 rounded-md">
                    <Mail className="h-4 w-4 mr-2" />
                    The candidate will be automatically notified via email.
                  </p>
                )}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Quick Move <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="quick-move-reason-input"
                    value={quickMoveReason}
                    onChange={(e) => setQuickMoveReason(e.target.value)}
                    placeholder="Enter reason for this move (min 10 characters)..."
                    rows={3}
                    maxLength={1500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{quickMoveReason.length}/1500</p>
                  {quickMoveReason.trim().length > 0 && quickMoveReason.trim().length < 10 && (
                    <p className="text-xs text-red-500 mt-1">Minimum 10 characters required.</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowQuickMoveConfirm(false);
                    setCandidateForQuickMove(null);
                    setQuickMoveTargetStage(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!quickMoveReason.trim() || quickMoveReason.trim().length < 10) {
                      toast.error('Please provide a reason for this move (minimum 10 characters).');
                      return;
                    }
                    if (candidateForQuickMove && quickMoveTargetStage) {
                      // Use Quick Move - bypass validation by directly calling the mutation with skipValidation
                      const candidateId = candidateForQuickMove.id || candidateForQuickMove._id || candidateForQuickMove.candidateId;
                      if (candidateId) {
                        // For Quick Move, directly move to any stage (including offer) without opening forms
                        moveCandidateMutation.mutate({
                          candidateId,
                          newStage: quickMoveTargetStage,
                          skipValidation: true,
                          reason: quickMoveReason.trim()
                        });
                      } else {
                        toast.error('Candidate identifier missing. Please refresh and try again.');
                      }
                    }
                    setShowQuickMoveConfirm(false);
                    setCandidateForQuickMove(null);
                    setQuickMoveTargetStage(null);
                    setQuickMoveReason('');
                  }}
                  className={quickMoveTargetStage === 'rejected'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                  }
                  disabled={loading || moveCandidateMutation.isPending || quickMoveReason.trim().length < 10}
                >
                  {loading || moveCandidateMutation.isPending ? 'Processing...' : 'Confirm Move'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      <ScheduleInterviewModal
        isOpen={showScheduleInterview}
        onClose={() => {
          setShowScheduleInterview(false);
          setCandidateForInterview(null);
        }}
        onSuccess={(result) => {
          queryClient.invalidateQueries(['interviews']);
          queryClient.invalidateQueries(['candidates']);
        }}
        candidates={candidates}
        preselectedCandidate={candidateForInterview}
      />

      {/* Interview Requirement Warning */}
      {interviewWarning.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-blue-500 mx-auto" />
            <h3 className="text-xl font-semibold text-gray-900">Action Required</h3>
            <p className="text-gray-700 leading-relaxed">
              {interviewWarning.message ||
                WORKFLOW_WARNINGS.hired}
            </p>
            <Button
              variant="primary"
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => setInterviewWarning({ show: false, message: '' })}
            >
              Got it
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Delete Selected Candidates</h3>
                </div>
              </div>
              <div className="mb-6 space-y-2">
                <p className="text-sm text-gray-600">
                  You are about to permanently delete{' '}
                  <span className="font-semibold text-red-600">{selectedRows.size} candidate(s)</span>.
                </p>
                <p className="text-sm text-gray-500">
                  This action cannot be undone. All candidate data, including resumes and application history, will be permanently deleted.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-blue-50/30 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                >
                  {bulkDeleteLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedRows.size} Candidate(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Candidates"
        showDateRange
        scopedFormatExport={{
          onExport: handlePipelineScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default CandidatePipeline;
